import { normalize, schema } from "normalizr"
import uuid from "uuid/v1"
import dayjs from "dayjs"
import apiRequest from "../../../Utils/apiRequest"
import types from "../types"
import {
  buildWorkshopLocation,
  isCourseBuilderWorkshop
} from "../../../Utils/workshopNavigation"
import { ADDITIONAL_COURSE_ID } from "../../../Constants/courses"
import { preconnectModuleImageOrigins } from "../../../Utils/preconnectModuleImageOrigins"
import {
  countPersistedButtonSelections,
  isButtonSelectionRequirementMet
} from "../../../Utils/buttonSelectionLimit"

const partDataSchema = new schema.Entity("partData", {})
const navigationPartSchema = new schema.Entity("navigationPart", {})

const elementSchema = new schema.Entity("elements", {})
const partSchema = new schema.Entity("parts", {
  elements: [elementSchema],
  partData: partDataSchema
})

const moduleSchema = new schema.Entity("modules", {
  parts: [partSchema],
  navigationParts: [navigationPartSchema]
})

const courseSchema = new schema.Entity("courses", {
  modules: [moduleSchema]
})

// const videoSchema = new schema.Entity("videos", {})
// const categorySchema = new schema.Entity("categories", {})

// const typeSchema = new schema.Entity("types", {
//   videos: [videoSchema]
// })

const fetch = (url, params, requestPayload) => ({
  type: "FETCH",
  url,
  params,
  requestPayload: {
    timeout: 20000
  }
})

const internals = {}

internals.getInputTitleId = (box) => Number(box?.input_title_id ?? box?.inputTitleId)

internals.resolveBoxInput = (inputBoxes, inputTitle, index, claimedBoxIds, validTitleIds) => {
  const titleId = Number(inputTitle.id)
  const boxes = (inputBoxes || []).filter(
    (row) => row.id && !String(row.id).startsWith("temp_")
  )

  const byId = boxes.find((box) => {
    const boxTitleId = internals.getInputTitleId(box)
    return !Number.isNaN(boxTitleId) && boxTitleId === titleId
  })
  if (byId) return byId

  if (index < boxes.length) {
    const candidate = boxes[index]
    if (candidate && candidate.id && !claimedBoxIds.has(candidate.id)) {
      const boxTitleId = internals.getInputTitleId(candidate)
      const isOrphan =
        Number.isNaN(boxTitleId) || !validTitleIds.has(boxTitleId)
      if (isOrphan) return candidate
    }
  }

  return (
    boxes.find((box) => {
      if (!box.id || claimedBoxIds.has(box.id)) return false
      const boxTitleId = internals.getInputTitleId(box)
      return Number.isNaN(boxTitleId) || !validTitleIds.has(boxTitleId)
    }) || null
  )
}

internals.pickPreferredInputText = (tempText, existingText, apiText) => {
  if (tempText !== undefined && tempText !== null && String(tempText).length > 0) {
    return String(tempText)
  }
  if (
    existingText !== undefined &&
    existingText !== null &&
    String(existingText).length > 0
  ) {
    return String(existingText)
  }
  if (tempText !== undefined && tempText !== null) {
    return String(tempText)
  }
  if (existingText !== undefined && existingText !== null) {
    return String(existingText)
  }
  if (apiText !== undefined && apiText !== null) {
    return String(apiText)
  }
  return ""
}

internals.prepareInputsForSave = (partData) => {
  const inputTitles = partData.inputTitle || []
  const validTitleIds = new Set(inputTitles.map((t) => Number(t.id)))
  const rawInputs = partData.inputs || []

  const tempByTitleId = {}
  rawInputs.forEach((row) => {
    if (typeof row.id === "string" && row.id.startsWith("temp_")) {
      tempByTitleId[row.id.replace("temp_", "")] = row.text
    }
  })

  const realInputs = rawInputs.filter(
    (row) => !(typeof row.id === "string" && row.id.startsWith("temp_"))
  )

  const claimedBoxIds = new Set()
  let merged = inputTitles.length
    ? inputTitles.map((title, index) => {
        const resolved = internals.resolveBoxInput(
          realInputs,
          title,
          index,
          claimedBoxIds,
          validTitleIds
        )
        if (resolved?.id) claimedBoxIds.add(resolved.id)
        if (!resolved) return null
        const titleId = Number(title.id)
        const tempText = tempByTitleId[String(titleId)]
        const text = internals.pickPreferredInputText(
          tempText,
          resolved.text,
          undefined
        )
        return { ...resolved, text }
      }).filter(Boolean)
    : realInputs.map((row) => {
        const tid = internals.getInputTitleId(row)
        const tempText = tempByTitleId[String(tid)]
        const text = internals.pickPreferredInputText(
          tempText,
          row.text,
          undefined
        )
        return { ...row, text }
      })

  realInputs.forEach((row) => {
    const ubvId = row.user_button_value_id ?? row.userButtonValueId
    if (ubvId && row.id && !claimedBoxIds.has(row.id)) {
      claimedBoxIds.add(row.id)
      merged.push(row)
    }
  })

  return { mergedInputs: merged, validTitleIds }
}

internals.buildPartDataWithInputs = (pyp, partDataId, nextInputs, currentElements) => {
  const currentPartData = pyp.partData?.byId?.[partDataId]
  if (!currentPartData) return null
  const partData = {
    ...pyp.partData,
    byId: {
      ...pyp.partData.byId,
      [partDataId]: {
        ...currentPartData,
        data: {
          ...(currentPartData.data || {}),
          inputs: nextInputs
        }
      }
    }
  }
  return {
    partData,
    currentPartComplete: partIsComplete(
      currentElements,
      partData.byId[partDataId].data,
      isCourseBuilderWorkshop(pyp)
    )
  }
}

internals.resolveCurrentModuleId = (pyp) => {
  const nav = Number(pyp?.navigation?.currentModuleId)
  if (Number.isFinite(nav) && nav > 0) {
    return nav
  }
  const idx = getCurrentModuleIndex(
    pyp?.modules,
    pyp?.navigation?.currentModuleId
  )
  if (idx >= 0) {
    const fromList = Number(pyp.modules?.byId?.[pyp.modules.allIds[idx]]?.id)
    if (Number.isFinite(fromList) && fromList > 0) {
      return fromList
    }
  }
  const allIds = pyp?.modules?.allIds || []
  if (allIds.length === 1) {
    const only = Number(pyp.modules.byId[allIds[0]]?.id ?? allIds[0])
    if (Number.isFinite(only) && only > 0) {
      return only
    }
  }
  return null
}

internals.syncUserInputsFromButtonSelections = async (
  dispatch,
  partId,
  insertions,
  deletions,
  partData,
  insertionsOnly = false
) => {
  if (!partId) return

  const toCreate = []
  const seen = new Set()
  const addSelection = (sel) => {
    if (!sel || sel.id == null) return
    const key = `${!!sel.isCustom}-${sel.id}`
    if (seen.has(key)) return
    seen.add(key)
    toCreate.push({ id: sel.id, isCustom: !!sel.isCustom })
  }

  ;(insertions || []).forEach(addSelection)

  if (!insertionsOnly && partData?.buttonValues) {
    partData.buttonValues.forEach((bv) => {
      if (bv?.buttonSelections?.length) {
        addSelection({ id: bv.id, isCustom: !!bv.isCustom })
      }
    })
  }

  for (const sel of toCreate) {
    try {
      await dispatch(
        fetch(`/userInputs`, {
          type: "POST",
          data: {
            requestPayload: {
              partId,
              buttonId: sel.isCustom ? undefined : sel.id,
              customButtonId: sel.isCustom ? sel.id : undefined
            }
          }
        })
      )
    } catch (e) {
      // Error handling
    }
  }

  if (!deletions || !deletions.length) return

  try {
    const inputsRes = await dispatch(
      fetch(`/userInputs`, {
        type: "GET",
        data: { params: { partId } }
      })
    )
    const inputs = inputsRes?.data || []
    for (const del of deletions) {
      for (const input of inputs) {
        const btnId =
          input.buttonSelectionTitle?.button_value_id ??
          input.buttonSelectionTitle?.buttonValue?.id
        const customId =
          input.buttonSelectionTitle?.custom_button_value_id ??
          input.buttonSelectionTitle?.customButtonValue?.id
        const matches = del.isCustom ? customId === del.id : btnId === del.id
        if (matches && input.id) {
          await dispatch(
            fetch(`/userInputs/${input.id}`, {
              type: "DELETE"
            })
          )
        }
      }
    }
  } catch (e) {
    // Error handling
  }
}

internals.resolveActiveCourseId = (pyp) => {
  const navCourseId = Number(pyp.navigation?.currentCourseId)
  if (navCourseId > 0) {
    return navCourseId
  }
  const { courses } = pyp
  if (courses?.allIds?.length && courses.byId) {
    const courseId = courses.byId[courses.allIds[0]]?.id
    if (courseId) {
      return Number(courseId)
    }
  }
  return null
}

internals.buildWorkshopLocation = buildWorkshopLocation

// used during increment, initialize, and de-increment part
internals.getElementString = (currentElements, pyp) => {
  return currentElements
    .map((element) => {
      if (element.name) {
        return element.name
      }
      return pyp.elements.byId[element].name
    })
    .sort()
    .toString()
}

export const exitModule = (history) => {
  return async (dispatch, getState) => {
    const { pyp } = getState()
    const moduleTitle =
      pyp.modules.byId[pyp.navigation.currentModuleId].title
    history.push(
      internals.buildWorkshopLocation(moduleTitle, pyp.navigation)
    )
    try {
      dispatch({
        type: types.EXIT_MODULE
      })
    } catch (data) {
      dispatch({
        type: types.EXCEPTION,
        data: data.response
      })
    }
  }
}

export const navigateToModule = (moduleTitle, history) => {
  return async (dispatch, getState) => {
    const { modules } = getState().pyp
    const decodedModuleTitle = decodeURIComponent(moduleTitle || "")
    const module = modules.allIds.find((m) => {
      const title = modules.byId[m].title
      return title === decodedModuleTitle || title === moduleTitle
    })
    dispatch({
      type: types.NAVIGATE_TO_MODULE,
      data: {
        moduleId: module
      }
    })
  }
}

const getCurrentPartIndex = (parts, partId) => {
  const target = Number(partId)
  return (parts?.allIds || []).findIndex((id) => Number(id) === target)
}

const getNextPartIndex = (currentPartId, parts, currentPartIndex) => {
  const allIds = parts?.allIds || []
  // Invalid index must not signal end-of-module (would skip remaining parts).
  if (currentPartIndex < 0 || currentPartIndex >= allIds.length) {
    return allIds.length > 0 ? 0 : -1
  }
  return currentPartIndex < allIds.length - 1 ? currentPartIndex + 1 : -1
}

const getCurrentModuleIndex = (modules, moduleId) => {
  const target = Number(moduleId)
  return (modules?.allIds || []).findIndex((id) => Number(id) === target)
}

const getPreviousPartIndex = (currentPartIndex) => {
  return currentPartIndex > 0 ? currentPartIndex - 1 : -1
}

const fetchMetaDataUserInputData = async (dispatch, partId) => {
  const inputTitlesRes = await dispatch(
    fetch(`/inputTitles`, {
      type: "GET",
      data: {
        params: { partId }
      }
    })
  )

  const inputsRes = await dispatch(
    fetch(`/userInputs`, {
      type: "GET",
      data: {
        params: {
          partId
        }
      }
    })
  )
  const titleRes = await dispatch(
    fetch(`/pypPartMetaData`, {
      type: "GET",
      data: {
        params: {
          partId
        }
      }
    })
  )

  const asyncApiCalls = await Promise.all([inputTitlesRes, inputsRes, titleRes])

  // Todo: Could set initializingPYPInputs to true here... Or set PYPInputsInitialized to false

  return {
    id: uuid(),
    data: {
      title: asyncApiCalls[2].data,
      inputTitle: asyncApiCalls[0].data,
      inputs: asyncApiCalls[1].data || []
    }
  }
}

const fetchButtonData = async (dispatch, partId, withReference) => {
  try {
    let btnPartId = partId
    if (withReference) {
      // Get all referenced parts for this particular part
      const referencedPartRes = await dispatch(
        fetch(`/partReferences`, {
          type: "GET",
          data: {
            params: { partId }
          }
        })
      )
      
      if (!referencedPartRes.data || !Array.isArray(referencedPartRes.data) || referencedPartRes.data.length === 0) {
        throw new Error(`No partReferences found for partId ${partId}`)
      }
      
      const firstReference = referencedPartRes.data[0]
      // Try multiple property names for part_reference_id (handles both snake_case and camelCase)
      btnPartId = firstReference?.part_reference_id || firstReference?.partReferenceId || firstReference?.part_referenceId
    }

    const buttonValuesRes = await dispatch(
      fetch(`/buttonValues`, {
        type: "GET",
        data: {
          params: {
            partId: btnPartId
          }
        }
      })
    )

    /*
 Custom Button Values are currently unused. It was a feature that was approved, but never released due to client concerns.
 It's possible it might make it back in, so we didn't remove the code.
*/
    // const customButtonValuesRes = await axios.get(
    //   `/customButtonValues/`,
    //   {
    //     headers: { Authorization: apiToken },
    //     params: { partId: btnPartId }
    //   }
    // );

    // const customButtonValues = customButtonValuesRes.data.map(value => {
    //   return { ...value, isCustom: true };
    // });

    const titleRes = await dispatch(
      fetch(`/pypPartMetaData`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    /*
 Custom Button Values are currently unused. It was a feature that was approved, but never released due to client concerns.
 It's possible it might make it back in, so we didn't remove the code.
*/
    return {
      id: uuid(),
      data: {
        buttonValues: [
          // ...customButtonValues,
          ...buttonValuesRes.data
        ],
        // customButtonValues: customButtonValues,
        title: titleRes.data
      }
    }
  } catch (e) {
    dispatch({
      type: types.EXCEPTION,
      data: e
    })
  }
}

const fetchMetaDataReference = async (dispatch, partId) => {
  try {
  const titleRes = await dispatch(
    fetch(`/pypPartMetaData`, {
      type: "GET",
      data: {
        params: {
          partId
        }
      }
    })
  )

  // Get all referenced parts for this particular part
  const referencedPartRes = await dispatch(
    fetch(`/partReferences`, {
      type: "GET",
      data: {
        params: {
          partId
        }
      }
    })
  )

  let referenceInputs = []
    // Try multiple ways to access the data
    const partReferencesData = referencedPartRes?.data || referencedPartRes || []
    const partReferencesArray = Array.isArray(partReferencesData) ? partReferencesData : []
    
    if (partReferencesArray.length > 0) {
      for (let i = 0; i < partReferencesArray.length; i++) {
        const part = partReferencesArray[i]
        
        // fetch the referenced part - try multiple property names
        const referencedPartId = part?.part_reference_id || part?.partReferenceId || part?.part_referenceId
        
        if (!referencedPartId) {
          continue
        }
        
        try {
          const fetchAction = fetch(`/userInputs`, {
        type: "GET",
        data: {
          params: {
                partId: referencedPartId
          }
        }
      })
          
          const referenceInputsRes = await dispatch(fetchAction)

          referenceInputs = [...referenceInputs, ...(referenceInputsRes.data || [])]
        } catch (error) {
          // Error fetching userInputs for referenced part
        }
      }
  }

  // TODO: All the above could be done with just two asynchronous API calls!
  return {
    id: uuid(),
    data: {
      title: titleRes.data,
      referenceInputs
      }
    }
  } catch (error) {
    return {
      id: uuid(),
      data: {
        title: null,
        referenceInputs: []
      }
    }
  }
}

const fetchMetaDataUserInput = async (dispatch, partId) => {
  // 1) Get title META DATA
  const titleRes = await dispatch(
    fetch(`/pypPartMetaData`, {
      type: "GET",
      data: {
        params: {
          partId
        }
      }
    })
  )

  // 2) Get REFERENCES to previous input cards
  // Get all referenced parts for this particular part

  const referencedPartRes = await dispatch(
    fetch(`/partReferences`, {
      type: "GET",
      data: {
        params: {
          partId
        }
      }
    })
  )

  let referenceInputs = []
  const partReferencesArray = Array.isArray(referencedPartRes?.data)
    ? referencedPartRes.data
    : []
  for (const part of partReferencesArray) {
    // fetch the referenced part
    // Try multiple property names for part_reference_id (handles both snake_case and camelCase)
    const referencedPartId = part?.part_reference_id || part?.partReferenceId || part?.part_referenceId

    const referenceInputsRes = await dispatch(
      fetch(`/userInputs`, {
        type: "GET",
        data: {
          params: {
            partId: referencedPartId
          }
        }
      })
    )

    referenceInputs = [...referenceInputs, ...referenceInputsRes.data]
  }

  // 3) Get data for inputs - INPUT TITLES & USER INPUTS
  const inputTitlesRes = await dispatch(
    fetch(`/inputTitles`, {
      type: "GET",
      data: {
        params: {
          partId
        }
      }
    })
  )

  const inputsRes = await dispatch(
    fetch(`/userInputs`, {
      type: "GET",
      data: {
        params: {
          partId
        }
      }
    })
  )

  return {
    id: uuid(),
    data: {
      title: titleRes.data,
      referenceInputs,
      inputTitle: inputTitlesRes.data,
      inputs: inputsRes.data || []
    }
  }
}

const apiMap = {
  "Button Selection Stage 2,Meta Data": async (dispatch, partId) => {
    return fetchButtonData(dispatch, partId, true)
  },
  "Button Selection,Meta Data": async (dispatch, partId) => {
    return fetchButtonData(dispatch, partId, false)
  },
  Video: async (dispatch, partId) => {
    const videoRes = await dispatch(
      fetch(`/pypVideos`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    return {
      id: uuid(),
      data: {
        video: videoRes.data
      }
    }
  },
  Instruction: async (dispatch, partId) => {
    const instructionRes = await dispatch(
      fetch(`/instructions`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    return {
      id: uuid(),
      data: {
        instruction: instructionRes.data
      }
    }
  },
  "Meta Data": async (dispatch, partId) => {
    const titleRes = await dispatch(
      fetch(`/pypPartMetaData`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    return {
      id: uuid(),
      data: {
        title: titleRes.data
      }
    }
  },
  "Meta Data,User Input Large": async (dispatch, partId) => {
    return fetchMetaDataUserInputData(dispatch, partId)
  },
  "Meta Data,User Input Medium": async (dispatch, partId) => {
    return fetchMetaDataUserInputData(dispatch, partId)
  },
  "Meta Data,User Input Small,User Input Small,User Input Small": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInputData(dispatch, partId)
  },
  "Meta Data,User Input Small": async (dispatch, partId) => {
    return fetchMetaDataUserInputData(dispatch, partId)
  },

  "Meta Data,Reference Lead Selection Small": async (dispatch, partId) => {
    return fetchMetaDataReference(dispatch, partId)
  },

  "Meta Data,Reference Box Single Small": async (dispatch, partId) => {
    return fetchMetaDataReference(dispatch, partId)
  },
  "Meta Data,Reference Box Single Medium": async (dispatch, partId) => {
    return fetchMetaDataReference(dispatch, partId)
  },
  "Meta Data,Reference Box Single Large": async (dispatch, partId) => {
    return fetchMetaDataReference(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Small": async (dispatch, partId) => {
    return fetchMetaDataReference(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Medium": async (dispatch, partId) => {
    return fetchMetaDataReference(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Large": async (dispatch, partId) => {
    return fetchMetaDataReference(dispatch, partId)
  },

  // There's potentially 18 of these
  "Meta Data,Reference Box Single Small,User Input Small": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Single Small,User Input Medium": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Single Small,User Input Large": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },

  "Meta Data,Reference Box Single Medium,User Input Small": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Single Medium,User Input Medium": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Single Medium,User Input Large": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },

  "Meta Data,Reference Box Single Large,User Input Small": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Single Large,User Input Medium": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Single Large,User Input Large": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Small,User Input Small": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Small,User Input Medium": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Small,User Input Large": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },

  "Meta Data,Reference Box Carousel Medium,User Input Small": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Medium,User Input Medium": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Medium,User Input Large": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },

  "Meta Data,Reference Box Carousel Large,User Input Small": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Large,User Input Medium": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },
  "Meta Data,Reference Box Carousel Large,User Input Large": async (
    dispatch,
    partId
  ) => {
    return fetchMetaDataUserInput(dispatch, partId)
  },

  // This Layout takes values entered from buttons in previous parts and renders user input boxes with titles from the button values
  "Meta Data,Reference User Input,User Input Small": async (
    dispatch,
    partId
  ) => {
    const titleRes = await dispatch(
      fetch(`/pypPartMetaData`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    // Get all referenced parts for this particular part
    // const referencedPartRes = await axios.get(`/partReferences/`, {
    //   headers: { Authorization: apiToken },
    //   params: { partId }
    // })
    const referencedPartRes = await dispatch(
      fetch(`/partReferences`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    // Here, assuming only one part is referenced:
    const firstReference = referencedPartRes.data[0]
    // Try multiple property names for part_reference_id (handles both snake_case and camelCase)
    const referencedPartId = firstReference?.part_reference_id || firstReference?.partReferenceId || firstReference?.part_referenceId

    const buttonValuesRes = await dispatch(
      fetch(`/buttonValues`, {
        type: "GET",
        data: {
          params: {
            partId: referencedPartId
          }
        }
      })
    )

    // Fetch any existing user inputs:
    const inputsRes = await dispatch(
      fetch(`/userInputs`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    return {
      id: uuid(),
      data: {
        title: titleRes.data,
        buttonValues: buttonValuesRes.data,
        inputs: inputsRes.data || []
        // referenceInputs: referenceInputs
      }
    }
  },
  "Meta Data,Reference User Input,User Input Small,User Input Small,User Input Small": async (
    dispatch,
    partId
  ) => {
    const titleRes = await dispatch(
      fetch(`/pypPartMetaData`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    // Get all referenced parts for this particular part
    const referencedPartRes = await dispatch(
      fetch(`/partReferences`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    // Fetch any existing user inputs:
    const inputsRes = await dispatch(
      fetch(`/userInputs`, {
        type: "GET",
        data: {
          params: {
            partId
          }
        }
      })
    )

    const asyncApiCalls = await Promise.all([
      titleRes,
      referencedPartRes,
      inputsRes
    ])

    // Here, assuming only one part is referenced:
    const firstReference = asyncApiCalls[1].data[0]
    // Try multiple property names for part_reference_id (handles both snake_case and camelCase)
    const referencedPartId = firstReference?.part_reference_id || firstReference?.partReferenceId || firstReference?.part_referenceId

    const buttonValuesRes = await dispatch(
      fetch(`/buttonValues`, {
        type: "GET",
        data: {
          params: {
            partId: referencedPartId
          }
        }
      })
    )

    /*
 Custom Button Values are currently unused. It was a feature that was approved, but never released due to client concerns.
 It's possible it might make it back in, so we didn't remove the code.
*/

    // const customButtonValuesRes = await axios.get(
    //   `/customButtonValues/`,
    //   {
    //     headers: { Authorization: apiToken },
    //     params: { partId: referencedPartId }
    //   }
    // );
    // const customButtonValues = customButtonValuesRes.data.map(value => {
    //   return { ...value, isCustom: true };
    // });

    return {
      id: uuid(),
      data: {
        title: asyncApiCalls[0].data,
        buttonValues: [
          // ...customButtonValues,
          ...buttonValuesRes.data
        ],
        inputs: asyncApiCalls[2].data || []
        // referenceInputs: referenceInputs
      }
    }
  },
  "Meta Data,Roadmapper": async (dispatch, partId) => {
    try {
      // Validate partId before making requests
      if (!partId || partId === -1 || isNaN(Number(partId))) {
        return {
          id: uuid(),
          data: {
            partTitle: null,
            userTimeEvents: [],
            eventOrder: null,
            referencedPartData: {
              constraints: [],
              buttonValues: []
            }
          }
        }
      }
      
      const partTitleRes = await dispatch(
        fetch(`/pypPartMetaData`, {
          type: "GET",
          data: {
            params: {
              partId: Number(partId)
            }
          }
        })
      )
      
      // Fetch partReferences first to determine which partId to use for timeEvents and eventOrder
      const referencedPartRes = await dispatch(
        fetch(`/partReferences`, {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )

      // Determine which partId to use for timeEvents and eventOrder
      // Based on live site: third reference (index 2) is used for timeEvents/eventOrder
      // If no third reference, fall back to the current partId
      let timeEventsPartId = Number(partId)
      if (referencedPartRes.data && Array.isArray(referencedPartRes.data) && referencedPartRes.data.length >= 3) {
        const thirdReference = referencedPartRes.data[2]
        const thirdPartId = thirdReference?.partReferenceId || thirdReference?.part_reference_id || thirdReference?.part_referenceId || thirdReference?.partReference_id
        if (thirdPartId !== undefined && thirdPartId !== null) {
          const numericThirdPartId = Number(thirdPartId)
          if (!isNaN(numericThirdPartId) && numericThirdPartId > 0) {
            timeEventsPartId = numericThirdPartId
          }
        }
      }
      
      const timeEvents = await dispatch(
        fetch("/timeEvents", {
          type: "GET",
          data: {
            params: { partId: timeEventsPartId }
          }
        })
      )
      const eventOrder = await dispatch(
        fetch("/eventOrder", {
          type: "GET",
          data: {
            params: { partId: timeEventsPartId }
          }
        })
      )

      if (!referencedPartRes.data || !Array.isArray(referencedPartRes.data)) {
        return {
          id: uuid(),
          data: {
            partTitle: partTitleRes.data,
            userTimeEvents: timeEvents.data || [],
            eventOrder: eventOrder.data || null,
            referencedPartData: {
              constraints: [],
              buttonValues: []
            }
          }
        }
      }

      let constraintsRes = { data: [] }
      let buttonValuesRes = { data: [] }

      // Get constraints from first referenced part (if available)
      // The first reference typically contains time constraints (wake up/go to bed times)
      if (referencedPartRes.data.length >= 1) {
        const firstReference = referencedPartRes.data[0]
        // Handle both camelCase (partReferenceId) and snake_case (part_reference_id) formats
        const constraintsPartId = firstReference?.partReferenceId || firstReference?.part_reference_id || firstReference?.part_referenceId || firstReference?.partReference_id
        
        if (constraintsPartId !== undefined && constraintsPartId !== null) {
          const numericConstraintsPartId = Number(constraintsPartId)
          if (!isNaN(numericConstraintsPartId) && numericConstraintsPartId > 0) {
            try {
              constraintsRes = await dispatch(
        fetch("/inputSingleLine", {
          type: "GET",
          data: {
            params: {
                      partId: numericConstraintsPartId
            }
          }
        })
      )
            } catch (e) {
              // If constraints fetch fails, continue with empty constraints
            }
          }
        }
      }

      // Get buttonValues from second referenced part (Stage 2 selections) if available
      // The second reference typically contains the button values (the 3 selected values from partId=21)
      if (referencedPartRes.data.length >= 2) {
        const secondReference = referencedPartRes.data[1]
        // Handle both camelCase (partReferenceId) and snake_case (part_reference_id) formats
        const buttonValuesPartId = secondReference?.partReferenceId || secondReference?.part_reference_id || secondReference?.part_referenceId || secondReference?.partReference_id
        
        if (buttonValuesPartId !== undefined && buttonValuesPartId !== null) {
          const numericButtonValuesPartId = Number(buttonValuesPartId)
          if (!isNaN(numericButtonValuesPartId) && numericButtonValuesPartId > 0) {
            try {
              buttonValuesRes = await dispatch(
        fetch(`/buttonValues`, {
          type: "GET",
          data: {
            params: {
                      partId: numericButtonValuesPartId
            }
          }
        })
      )
            } catch (e) {
              // If buttonValues fetch fails, continue with empty buttonValues
            }
          }
        }
      }

      return {
        id: uuid(),
        data: {
          partTitle: partTitleRes.data,
          userTimeEvents: timeEvents.data || [],
          eventOrder: eventOrder.data || null,
          referencedPartData: {
            constraints: constraintsRes.data || [],
            buttonValues: buttonValuesRes.data || []
          }
        }
      }
    } catch (e) {
      // Return minimal data structure to prevent page from breaking
      return {
        id: uuid(),
        data: {
          partTitle: null,
          userTimeEvents: [],
          eventOrder: null,
          referencedPartData: {
            constraints: [],
            buttonValues: []
          }
        }
      }
    }
  },
  "Action Planner,Meta Data": async (dispatch, partId) => {
    try {
      const partTitleRes = await dispatch(
        fetch(`/pypPartMetaData`, {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )
      let dateEvents = await dispatch(
        fetch("/dateEvent", {
          type: "GET",
          data: {
            params: { partId }
          }
        })
      )
      if (!dateEvents.data.length) {
        dateEvents = await dispatch(
          fetch(`/defaultDateEvents`, {
            type: "POST",
            data: {
              requestPayload: { partId }
            }
          })
        )
      }
      const eventOrder = await dispatch(
        fetch("/eventOrder", {
          type: "GET",
          data: {
            params: { partId }
          }
        })
      )
      const referencedPartRes = await dispatch(
        fetch(`/partReferences`, {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )

      const constraintsRes = await dispatch(
        fetch("/inputSingleLine", {
          type: "GET",
          data: {
            params: {
              partId: referencedPartRes.data[0].part_reference_id
            }
          }
        })
      )
      return {
        id: uuid(),
        data: {
          partTitle: partTitleRes.data,
          userDateEvents: dateEvents.data,
          eventOrder: eventOrder.data,
          referencedPartData: {
            constraints: constraintsRes.data
          }
        }
      }
    } catch (e) {
      // Error handling
    }
  },
  "Constraints,Meta Data": async (dispatch, partId) => {
    try {
      const partTitleRes = await dispatch(
        fetch(`/pypPartMetaData`, {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )
      const constraintsRes = await dispatch(
        fetch("/inputSingleLine", {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )
      return {
        id: uuid(),
        data: {
          partTitle: partTitleRes.data,
          constraints: constraintsRes.data
        }
      }
    } catch (e) {
      // Error handling
    }
  },
  "Bargraph,Meta Data": async (dispatch, partId) => {
    try {
      console.log('[Bargraph API] Starting fetch for partId:', partId)
      // Validate partId before making requests
      if (!partId || partId === -1 || isNaN(Number(partId))) {
        console.warn('[Bargraph API] Invalid partId:', partId)
        return {
          id: uuid(),
          data: {
            partTitle: null,
            referencedPartData: {
              constraints: [],
              buttonValues: [],
              timeEvents: []
            }
          }
        }
      }
      
      console.log('[Bargraph API] Fetching partTitle for partId:', partId)
      const partTitleRes = await dispatch(
        fetch(`/pypPartMetaData`, {
          type: "GET",
          data: {
            params: {
              partId: Number(partId)
            }
          }
        })
      )
      console.log('[Bargraph API] partTitle fetched:', partTitleRes.data)
      
      console.log('[Bargraph API] Fetching partReferences for partId:', partId)
      const referencedPartRes = await dispatch(
        fetch(`/partReferences`, {
          type: "GET",
          data: {
            params: {
              partId: Number(partId)
            }
          }
        })
      )
      console.log('[Bargraph API] partReferences fetched:', referencedPartRes.data?.length || 0, 'references')
      
      if (!referencedPartRes.data || !Array.isArray(referencedPartRes.data) || referencedPartRes.data.length < 3) {
        console.warn('[Bargraph API] Invalid referencedPartRes.data:', {
          isArray: Array.isArray(referencedPartRes.data),
          length: referencedPartRes.data?.length,
          data: referencedPartRes.data
        })
        return {
          id: uuid(),
          data: {
            partTitle: partTitleRes.data,
            referencedPartData: {
              constraints: [],
              buttonValues: [],
              timeEvents: []
            }
          }
        }
      }
      
      console.log('[Bargraph API] Processing references:', referencedPartRes.data.map((ref, idx) => ({
        index: idx,
        partReferenceId: ref.partReferenceId || ref.part_reference_id || ref.part_referenceId || ref.partReference_id
      })))
      
      // [0]: constraints (partId=76)
      // [1]: buttons (partId=20) 
      // [2]: timeEvents (partId=77)
      let constraintsRes = { data: [] }
      let buttonValuesRes = { data: [] }
      let timeEventsRes = { data: [] }
      
      // Get constraints from first reference
      const firstReference = referencedPartRes.data[0]
      const constraintsPartId = firstReference?.partReferenceId || firstReference?.part_reference_id || firstReference?.part_referenceId || firstReference?.partReference_id
      console.log('[Bargraph API] Fetching constraints from partId:', constraintsPartId)
      if (constraintsPartId !== undefined && constraintsPartId !== null) {
        const numericConstraintsPartId = Number(constraintsPartId)
        if (!isNaN(numericConstraintsPartId) && numericConstraintsPartId > 0) {
          try {
            constraintsRes = await dispatch(
        fetch("/inputSingleLine", {
          type: "GET",
          data: {
            params: {
                    partId: numericConstraintsPartId
            }
          }
        })
      )
            console.log('[Bargraph API] Constraints fetched:', constraintsRes.data?.length || 0, 'items')
          } catch (e) {
            console.error('[Bargraph API] Failed to fetch constraints:', e)
            // If constraints fetch fails, continue with empty constraints
          }
        }
      }
      
      // Get buttonValues from second reference
      const secondReference = referencedPartRes.data[1]
      const buttonValuesPartId = secondReference?.partReferenceId || secondReference?.part_reference_id || secondReference?.part_referenceId || secondReference?.partReference_id
      console.log('[Bargraph API] Fetching buttonValues from partId:', buttonValuesPartId)
      if (buttonValuesPartId !== undefined && buttonValuesPartId !== null) {
        const numericButtonValuesPartId = Number(buttonValuesPartId)
        if (!isNaN(numericButtonValuesPartId) && numericButtonValuesPartId > 0) {
          try {
            buttonValuesRes = await dispatch(
        fetch(`/buttonValues`, {
          type: "GET",
          data: {
            params: {
                    partId: numericButtonValuesPartId
            }
          }
        })
      )
            console.log('[Bargraph API] ButtonValues fetched:', buttonValuesRes.data?.length || 0, 'items')
          } catch (e) {
            console.error('[Bargraph API] Failed to fetch buttonValues:', e)
            // If buttonValues fetch fails, continue with empty buttonValues
          }
        }
      }
      
      // Get timeEvents from third reference
      const thirdReference = referencedPartRes.data[2]
      const timeEventsPartId = thirdReference?.partReferenceId || thirdReference?.part_reference_id || thirdReference?.part_referenceId || thirdReference?.partReference_id
      console.log('[Bargraph API] Fetching timeEvents from partId:', timeEventsPartId)
      if (timeEventsPartId !== undefined && timeEventsPartId !== null) {
        const numericTimeEventsPartId = Number(timeEventsPartId)
        if (!isNaN(numericTimeEventsPartId) && numericTimeEventsPartId > 0) {
          try {
            timeEventsRes = await dispatch(
        fetch("/timeEvents", {
          type: "GET",
          data: {
            params: {
                    partId: numericTimeEventsPartId
            }
          }
        })
      )
            console.log('[Bargraph API] TimeEvents fetched:', timeEventsRes.data?.length || 0, 'items')
          } catch (e) {
            console.error('[Bargraph API] Failed to fetch timeEvents:', e)
            // If timeEvents fetch fails, continue with empty timeEvents
          }
        }
      }
      
      const result = {
        id: uuid(),
        data: {
          partTitle: partTitleRes.data,
          referencedPartData: {
            constraints: constraintsRes.data || [],
            buttonValues: buttonValuesRes.data || [],
            timeEvents: timeEventsRes.data || []
          }
        }
      }
      console.log('[Bargraph API] Returning data:', {
        constraintsCount: result.data.referencedPartData.constraints.length,
        buttonValuesCount: result.data.referencedPartData.buttonValues.length,
        timeEventsCount: result.data.referencedPartData.timeEvents.length
      })
      return result
    } catch (e) {
      console.error('[Bargraph API] Error in bargraph fetch:', e)
      console.error('[Bargraph API] Error stack:', e.stack)
      // Return minimal data structure to prevent page from breaking
      return {
        id: uuid(),
        data: {
          partTitle: null,
          referencedPartData: {
            constraints: [],
            buttonValues: [],
            timeEvents: []
          }
        }
      }
    }
  },
  "Calender,Meta Data": async (dispatch, partId) => {
    try {
      const partTitleRes = await dispatch(
        fetch(`/pypPartMetaData`, {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )
      const referencedPartRes = await dispatch(
        fetch(`/partReferences`, {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )

      console.log('[Calender,Meta Data] Part references response:', {
        accountabilityPartId: partId,
        referencedPartsCount: referencedPartRes.data?.length || 0,
        referencedParts: referencedPartRes.data || [],
        firstReference: referencedPartRes.data?.[0],
        secondReference: referencedPartRes.data?.[1]
      })

      if (!referencedPartRes.data || !Array.isArray(referencedPartRes.data) || referencedPartRes.data.length < 2) {
        console.error('[Calender,Meta Data] Invalid part references! Expected at least 2 references, got:', referencedPartRes.data)
        return {
          id: uuid(),
          data: {
            partTitle: partTitleRes.data,
            referencedPartData: {
              dateEvents: [],
              start: { title: "Today", time: null },
              end: { title: "My Next Chapter", time: null }
            }
          }
        }
      }

      const constraintsPartId = referencedPartRes.data[0]?.part_reference_id || referencedPartRes.data[0]?.partReferenceId
      console.log('[Calender,Meta Data] Fetching constraints from partId:', constraintsPartId)

      const constraintsRes = await dispatch(
        fetch("/inputSingleLine", {
          type: "GET",
          data: {
            params: {
              partId: constraintsPartId
            }
          }
        })
      )
      
      console.log('[Calender,Meta Data] Constraints response:', {
        partId: constraintsPartId,
        constraintsCount: constraintsRes.data?.length || 0,
        constraints: constraintsRes.data || []
      })

      // Get the partId for date events (should be the second referenced part, which is partId=73)
      const dateEventsPartId = referencedPartRes.data[1]?.part_reference_id || referencedPartRes.data[1]?.partReferenceId
      
      console.log('[Calender,Meta Data] Fetching date events:', {
        referencedPartsCount: referencedPartRes.data?.length || 0,
        dateEventsPartId: dateEventsPartId,
        secondReference: referencedPartRes.data[1],
        usingPartId: dateEventsPartId
      })
      
      if (!dateEventsPartId) {
        console.error('[Calender,Meta Data] No dateEventsPartId found! referencedPartRes.data:', referencedPartRes.data)
        return {
          id: uuid(),
          data: {
            partTitle: partTitleRes.data,
            referencedPartData: {
              dateEvents: [],
              start: { 
                title: "Today", 
                time: constraintsRes.data?.[0]?.text || null 
              },
              end: { 
                title: "My Next Chapter", 
                time: constraintsRes.data?.[1]?.text || null 
              }
            }
          }
        }
      }

      const dateEventsRes = await dispatch(
        fetch("/dateEvent", {
          type: "GET",
          data: {
            params: {
              partId: dateEventsPartId
            }
          }
        })
      )
      
      console.log('[Calender,Meta Data] Date events response:', {
        requestedPartId: dateEventsPartId,
        eventsCount: dateEventsRes.data?.length || 0,
        events: dateEventsRes.data || [],
        firstEvent: dateEventsRes.data?.[0]
      })

      // Get constraint dates for validation and normalize them
      let constraintStart = constraintsRes.data[0]?.text
      let constraintEnd = constraintsRes.data[1]?.text
      
      // Normalize constraint dates - convert YYYY:MM:DD to YYYY-MM-DD if needed
      if (constraintStart && typeof constraintStart === 'string' && constraintStart.match(/^\d{4}:\d{2}:\d{2}$/)) {
        constraintStart = constraintStart.replace(/:/g, '-')
      }
      if (constraintEnd && typeof constraintEnd === 'string' && constraintEnd.match(/^\d{4}:\d{2}:\d{2}$/)) {
        constraintEnd = constraintEnd.replace(/:/g, '-')
      }
      
      // Filter and map date events - only include events with valid dates within bounds
      const validDateEvents = (dateEventsRes.data || []).map((item) => {
        // Normalize moment_data (handle both moment_data and momentData)
        let moment_data = item.moment_data || item.momentData || null
        // Normalize date format - convert YYYY:MM:DD to YYYY-MM-DD if needed
        if (moment_data && typeof moment_data === 'string' && moment_data.match(/^\d{4}:\d{2}:\d{2}$/)) {
          moment_data = moment_data.replace(/:/g, '-')
        }
        return {
          ...item,
          moment_data: moment_data
        }
      }).filter((item) => {
        // Only include events that have a valid date
        if (!item.moment_data) {
          console.log('[Calender,Meta Data] Filtered out event (no date):', item.title)
          return false
        }
        
        // If constraints exist, validate that the date is within bounds
        if (constraintStart && constraintEnd) {
          const parsedDate = dayjs(item.moment_data, ['YYYY-MM-DD', 'YYYY:MM:DD', 'YYYY/MM/DD'], true)
          if (!parsedDate.isValid()) {
            const autoParsed = dayjs(item.moment_data)
            if (!autoParsed.isValid()) {
              console.log('[Calender,Meta Data] Filtered out event (invalid date):', item.title, item.moment_data)
              return false
            }
            const constraintStartDate = dayjs(constraintStart, ['YYYY-MM-DD', 'YYYY:MM:DD'], true)
            const constraintEndDate = dayjs(constraintEnd, ['YYYY-MM-DD', 'YYYY:MM:DD'], true)
            if (!constraintStartDate.isValid() || !constraintEndDate.isValid()) {
              console.log('[Calender,Meta Data] Invalid constraints, including event:', item.title)
              return true // Include if constraints are invalid
            }
            // Use isSameOrAfter and isSameOrBefore for inclusive bounds checking
            const isWithinBounds = (autoParsed.isSame(constraintStartDate) || autoParsed.isAfter(constraintStartDate)) && 
                                   (autoParsed.isSame(constraintEndDate) || autoParsed.isBefore(constraintEndDate))
            if (!isWithinBounds) {
              console.log('[Calender,Meta Data] Filtered out event (out of bounds):', item.title, {
                date: item.moment_data,
                parsed: autoParsed.format('YYYY-MM-DD'),
                start: constraintStartDate.format('YYYY-MM-DD'),
                end: constraintEndDate.format('YYYY-MM-DD')
              })
            }
            return isWithinBounds
          }
          const constraintStartDate = dayjs(constraintStart, ['YYYY-MM-DD', 'YYYY:MM:DD'], true)
          const constraintEndDate = dayjs(constraintEnd, ['YYYY-MM-DD', 'YYYY:MM:DD'], true)
          if (!constraintStartDate.isValid() || !constraintEndDate.isValid()) {
            console.log('[Calender,Meta Data] Invalid constraints, including event:', item.title)
            return true // Include if constraints are invalid
          }
          // Use inclusive bounds checking - dates equal to start or end are valid
          const isWithinBounds = !parsedDate.isBefore(constraintStartDate) && !parsedDate.isAfter(constraintEndDate)
          if (!isWithinBounds) {
            console.log('[Calender,Meta Data] Filtered out event (out of bounds):', item.title, {
              date: item.moment_data,
              parsed: parsedDate.format('YYYY-MM-DD'),
              start: constraintStartDate.format('YYYY-MM-DD'),
              end: constraintEndDate.format('YYYY-MM-DD')
            })
          }
          return isWithinBounds
        }
        
        // If no constraints, include all events with dates
        console.log('[Calender,Meta Data] Including event (no constraints):', item.title)
        return true
      })

      console.log('[Calender,Meta Data] API call:', {
        totalEvents: dateEventsRes.data?.length || 0,
        validEvents: validDateEvents.length,
        constraintStart,
        constraintEnd,
        constraintStartFormatted: constraintStart ? dayjs(constraintStart, ['YYYY-MM-DD', 'YYYY:MM:DD'], true).format('YYYY-MM-DD') : 'N/A',
        constraintEndFormatted: constraintEnd ? dayjs(constraintEnd, ['YYYY-MM-DD', 'YYYY:MM:DD'], true).format('YYYY-MM-DD') : 'N/A',
        allEvents: dateEventsRes.data?.map(e => ({
          id: e.id,
          title: e.title,
          moment_data: e.moment_data,
          hasDate: !!e.moment_data
        })) || [],
        validEventsDetails: validDateEvents.map(e => ({
          id: e.id,
          title: e.title,
          moment_data: e.moment_data
        }))
      })

      // Normalize constraint dates for start/end (convert YYYY:MM:DD to YYYY-MM-DD if needed)
      let startTime = constraintsRes.data[0]?.text
      let endTime = constraintsRes.data[1]?.text
      if (startTime && typeof startTime === 'string' && startTime.match(/^\d{4}:\d{2}:\d{2}$/)) {
        startTime = startTime.replace(/:/g, '-')
      }
      if (endTime && typeof endTime === 'string' && endTime.match(/^\d{4}:\d{2}:\d{2}$/)) {
        endTime = endTime.replace(/:/g, '-')
      }

      return {
        id: uuid(),
        data: {
          partTitle: partTitleRes.data,
          referencedPartData: {
            dateEvents: validDateEvents.map((item) => ({
              title: item.title,
              time: item.moment_data
            })),
            start: {
              title: "Today",
              time: startTime
            },
            end: {
              title: "My Next Chapter",
              time: endTime
            }
          }
        }
      }
    } catch (error) {
      dispatch({
        type: types.EXCEPTION,
        data: error.response
      })
    }
  },
  "Meta Data,User Input Example": async (dispatch, partId) => {
    try {
      const partTitleRes = await dispatch(
        fetch(`/pypPartMetaData`, {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )

      const userInputExampleRes = await dispatch(
        fetch(`/userInputExamples`, {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )

      return {
        id: uuid(),
        data: {
          partTitle: partTitleRes.data,
          userInputExamples: userInputExampleRes.data
        }
      }
    } catch (e) {
      // Error handling
    }
  },
  default: async (dispatch, partId) => {
    return {
      id: uuid(),
      data: {}
    }
  }
}

export const shouldShowPypCompletionModal = ({
  hasCoach,
  isCompletingCurrentModule,
  isFinalModule,
  moduleAlreadyCompleted
}) => {
  // Show the modal exactly when the learner completes the final module for the first time.
  return Boolean(
    !hasCoach &&
      isCompletingCurrentModule &&
      isFinalModule &&
      !moduleAlreadyCompleted
  )
}

const isModuleMarkedCompleted = (moduleRecord) => {
  const completed = moduleRecord && moduleRecord.completed
  return completed === 1 || completed === true || completed === "1"
}

const isFinalPypModule = ({ moduleTitle, currentModuleIndex, moduleCount }) => {
  if (typeof moduleTitle === "string" && /action plan/i.test(moduleTitle)) {
    return true
  }
  return currentModuleIndex === moduleCount - 1
}

export const incrementPartId = (history) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.RESET_ERRORS })
      const {
        pyp,
        user: { user }
      } = getState()

      dispatch({
        type: types.INCREMENTING_PYP
      })

      const { currentPartId } = pyp.navigation
      const resolvedModuleId = internals.resolveCurrentModuleId(pyp)
      let nextModuleIndex
      const updatedUnlockedUserModules = pyp.unlockedUserModules

      const currentIndex = getCurrentPartIndex(pyp.parts, currentPartId)
      let nextIndex = getNextPartIndex(currentPartId, pyp.parts, currentIndex)
      const currentModuleIndex = getCurrentModuleIndex(
        pyp.modules,
        resolvedModuleId
      )
      const currentModule =
        resolvedModuleId &&
        pyp.modules.byId &&
        pyp.modules.byId[resolvedModuleId]
      const reachedEndOfCurrentModule = nextIndex === -1
      const isFinalModule = isFinalPypModule({
        moduleTitle: currentModule && currentModule.title,
        currentModuleIndex,
        moduleCount: pyp.modules.allIds.length
      })
      const isCourseBuilder = isCourseBuilderWorkshop(pyp)
      const findUnlockedModule = (list) =>
        (list || []).find(
          (unlockedModule) =>
            Number(unlockedModule.module_id) === Number(resolvedModuleId)
        )
      let currentUnlockedModule = findUnlockedModule(pyp.unlockedUserModules)

      if (!currentUnlockedModule) {
        const refreshedUserModulesRes = await dispatch(
          fetch(`/userModules`, {
            type: "GET"
          })
        )
        const refreshedUserModules = refreshedUserModulesRes.data || []
        currentUnlockedModule = findUnlockedModule(refreshedUserModules)
      }

      // Safety: ensure the current module has a user_modules row before completion updates.
      if (!currentUnlockedModule && reachedEndOfCurrentModule) {
        if (!resolvedModuleId) {
          dispatch({
            type: types.EXCEPTION,
            data: {
              message:
                "Cannot complete module: moduleId is missing from navigation"
            }
          })
          return
        }
        await dispatch(
          fetch(`/userModules`, {
            type: "POST",
            data: {
              requestPayload: {
                moduleId: resolvedModuleId
              }
            }
          })
        )
        const latestUserModulesRes = await dispatch(
          fetch(`/userModules`, {
            type: "GET"
          })
        )
        const latestUserModules = latestUserModulesRes.data || []
        currentUnlockedModule = findUnlockedModule(latestUserModules)
      }

      const currentUserModuleId =
        currentUnlockedModule && currentUnlockedModule.id
      const moduleAlreadyCompleted = isModuleMarkedCompleted(
        currentUnlockedModule
      )

      // We've navigated to the end of the module
      if (nextIndex === -1) {
        if (currentModuleIndex < pyp.modules.allIds.length - 1) {
          nextModuleIndex = currentModuleIndex + 1
        } else {
          nextModuleIndex = currentModuleIndex
          nextIndex = currentIndex
        }

        if (!moduleAlreadyCompleted && currentUserModuleId) {
          await dispatch(
            fetch(`/userModules/${currentUserModuleId}`, {
              type: "PUT",
              data: {
                requestPayload: {
                  completed: true,
                  started: true,
                  isFinalModule
                }
              }
            })
          )
        }

        // Course builder final module: mark complete, refresh user, go home.
        if (isFinalModule && isCourseBuilder) {
          const updatedUnlockedUserModulesRes = await dispatch(
            fetch(`/userModules`, {
              type: "GET"
            })
          )
          dispatch({
            type: types.INCREMENT_PART,
            data: {
              partId: pyp.parts.allIds[currentIndex],
              moduleId: resolvedModuleId,
              unlockedUserModules: updatedUnlockedUserModulesRes.data,
              partData: pyp.partData
            }
          })

          const userRes = await dispatch(
            fetch(`/user`, {
              type: "GET"
            })
          )
          dispatch({
            type: types.FETCHED_USER,
            user: userRes.data[0]
          })

          history.push("/home")
          return
        }

        if (!isFinalModule) {
          const nextModule =
            pyp.modules.byId[pyp.modules.allIds[nextModuleIndex]]
          const nextModuleId = Number(nextModule?.id)
          if (!Number.isFinite(nextModuleId) || nextModuleId < 1) {
            dispatch({
              type: types.EXCEPTION,
              data: {
                message: "Cannot unlock next module: moduleId is missing"
              }
            })
            return
          }
          await dispatch(
            fetch(`/userModules`, {
              type: "POST",
              data: {
                requestPayload: {
                  moduleId: nextModuleId
                }
              }
            })
          )
        }

        const updatedUnlockedUserModulesRes = await dispatch(
          fetch(`/userModules`, {
            type: "GET"
          })
        )

        history.push(
          buildWorkshopLocation(
            pyp.modules.byId[pyp.modules.allIds[nextModuleIndex]].title,
            pyp.navigation
          )
        )

        const nextPartId = pyp.parts.allIds[nextIndex]

        dispatch({
          type: types.INCREMENT_PART,
          data: {
            partId: nextPartId,
            moduleId: pyp.modules.allIds[nextModuleIndex],
            unlockedUserModules: updatedUnlockedUserModulesRes.data,
            partData: pyp.partData
          }
        })

        const userRes = await dispatch(
          fetch(`/user`, {
            type: "GET"
          })
        )

        dispatch({
          type: types.FETCHED_USER,
          user: userRes.data[0]
        })

        // navigating to the next part within the module
      } else {
        nextModuleIndex = currentModuleIndex

        dispatch({
          type: types.INCREMENT_PART,
          data: {
            partId: pyp.parts.allIds[nextIndex],
            moduleId:
              resolvedModuleId || pyp.modules.allIds[nextModuleIndex],
            unlockedUserModules: updatedUnlockedUserModules
          }
        })
      }

      const hasCoach = Boolean(user && user.pyp && user.pyp.has_coaching)
      if (
        shouldShowPypCompletionModal({
          hasCoach,
          isCompletingCurrentModule: reachedEndOfCurrentModule,
          isFinalModule,
          moduleAlreadyCompleted
        })
      ) {
        dispatch({
          type: types.TOGGLE_MODAL,
          data: {
            modal: {
              pypModal: true
            }
          }
        })
      }
    } catch (error) {
      console.error('[incrementPartId]', error?.response?.status, error?.response?.data || error?.message)
      // Use EXCEPTION, not ERROR: ERROR sets errors.hasError and ErrorRenderer replaces the whole workshop tree with FourOhFour.
      dispatch({
        type: types.EXCEPTION,
        data: error?.response || { message: error?.message, error }
      })
      // history.push('/');
    }
  }
}

export const fetchPartData = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.RESET_ERRORS })
      // Dispatch fetching action
      dispatch({
        type: types.FETCHING_PART_DATA
      })

      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      
      // Validate that we have a valid currentPartId and currentPart
      // currentPartId can be -1 (initial value) or undefined, both are invalid
      if (!currentPartId || currentPartId === -1) {
        dispatch({
          type: types.FETCHED_PART_DATA,
          data: {
            partData: { allIds: [], byId: {} },
            updatedPart: {},
            currentPartComplete: false
          }
        })
        return
      }
      
      const currentPart = pyp.parts?.byId?.[currentPartId]
      if (!currentPart) {
        dispatch({
          type: types.FETCHED_PART_DATA,
          data: {
            partData: { allIds: [], byId: {} },
            updatedPart: {},
            currentPartComplete: false
          }
        })
        return
      }
      
      const currentElements = currentPart?.elements || []

      const elementString = internals.getElementString(currentElements, pyp)
      console.log('[fetchPartData] Element string:', elementString, 'for partId:', currentPartId)
      const apiCall = apiMap[elementString] || apiMap.default
      
      if (!apiCall) {
        console.error('[fetchPartData] No API call found for element string:', elementString)
        dispatch({
          type: types.FETCHED_PART_DATA,
          data: {
            partData: { allIds: [], byId: {} },
            updatedPart: {},
            currentPartComplete: false
          }
        })
        return
      }
      
      // Final validation: ensure currentPartId is valid before calling API
      if (!currentPartId || currentPartId === -1 || isNaN(currentPartId)) {
        console.warn('[fetchPartData] Invalid currentPartId:', currentPartId)
        dispatch({
          type: types.FETCHED_PART_DATA,
          data: {
            partData: { allIds: [], byId: {} },
            updatedPart: {},
            currentPartComplete: false
          }
        })
        return
      }
      
      console.log('[fetchPartData] Calling API for partId:', currentPartId, 'elementString:', elementString)
      const data = await apiCall(dispatch, currentPartId)
      console.log('[fetchPartData] API call completed, data received:', !!data, {
        hasData: !!data?.data,
        hasReferencedPartData: !!data?.data?.referencedPartData,
        constraintsCount: data?.data?.referencedPartData?.constraints?.length || 0,
        buttonValuesCount: data?.data?.referencedPartData?.buttonValues?.length || 0,
        timeEventsCount: data?.data?.referencedPartData?.timeEvents?.length || 0
      })
      
      if (!data) {
        dispatch({
          type: types.FETCHED_PART_DATA,
          data: {
            partData: { allIds: [], byId: {} },
            updatedPart: {},
            currentPartComplete: false
          }
        })
        return
      }

      const part = pyp.parts.byId[currentPartId]
      // Create a copy of the part object instead of mutating the state directly
      const partWithData = { ...part, partData: data }

      const n = normalize(
        {
          parts: [partWithData],
          partData: [data]
        },
        {
          parts: [partSchema],
          partData: [partDataSchema]
        }
      )

      // Safely create elements array, filtering out undefined/null values
      const elements = currentPart && currentPart.elements && Array.isArray(currentPart.elements)
        ? currentPart.elements
            .map((elementId) => pyp.elements?.byId?.[elementId])
            .filter((element) => element !== undefined && element !== null)
        : []

      // // NOTE: This not ever happen anymore because now you can't increment parts until data has finished fetching...
      // // Important! By the time the api calls above are returned, a user could
      // // have clicked 'Next' or 'Previous' and changed the current part!
      // // This checks to ensure the user is on the same part the API call was triggered from.
      // const newCurrentPartId = getState().pyp.navigation.currentPartId
      // if(currentPartId !== newCurrentPartId) {
      //   // If now on a different part, return from the fuction without any dispatch
      //   // navigation.fetchingPartData will be left to true
      //   return
      // }

      dispatch({
        type: types.FETCHED_PART_DATA,
        data: {
          partData: {
            allIds: n.result.partData,
            byId: n.entities.partData
          },
          updatedPart: n.entities.parts,
          currentPartComplete: partIsComplete(
            elements,
            data.data,
            isCourseBuilderWorkshop(pyp)
          )
        }
      })

      // const newState = getState();
      // debugger;
      // Check if inputs have been intialized
      // if(data.data.hasOwnProperty('inputs') && !data.data.inputs.length) {
      //   const inputsToMake = currentElements
      //     .map(elementId => pyp.elements.byId[elementId])
      //     .filter(element => element.name.includes("User Input"));
      //
      //   // Iterate through each inputTitle and perform a POST to create a new inputTitle. Save each response within an array
      //   const userInputInsertions = inputsToMake.map(async (input, i) => {
      //     debugger;
      //     return await axios.post(
      //       `/userInputs`,
      //       {
      //         partId: currentPartId,
      //         titleId: data.data.inputTitle[i].id,
      //         size: getInputSize(input),
      //         text: ""
      //       },
      //       { headers: { Authorization: getState().api.apiToken } }
      //     );
      //   });
      //
      //   const inputsRes = await Promise.all(userInputInsertions); //.map(input => input.data)
      //
      //   dispatch({
      //     type: types.CREATE_PYP_INPUTS,
      //     data: {
      //       partDataId: data.id,
      //       inputs: inputsRes.map(input => input.data)
      //     }
      //   });
      // }
    } catch (error) {
      console.error('[fetchPartData]', error?.response?.status, error?.response?.data || error?.message)
      // Always reset fetchingPartData flag on error
      dispatch({
        type: types.FETCHED_PART_DATA,
        data: {
          partData: { allIds: [], byId: {} },
          updatedPart: {},
          currentPartComplete: false
        }
      })
      dispatch({
        type: types.EXCEPTION,
        data: error?.response || { message: error?.message, error }
      })
      // history.push('/');
    }
  }
}

export const fetchCourseWithModules = (courseNameOrId, options = {}) => {
  return async (dispatch) => {
    try {
      const courseRes = options.byId
        ? await dispatch(
            fetch(`/courses/${courseNameOrId}`, {
              type: "GET"
            })
          )
        : await dispatch(
            fetch(`/courses`, {
              type: "GET",
              data: {
                params: { name: courseNameOrId }
              }
            })
          )
      if (!courseRes?.data?.id) {
        return { course: null, modules: [] }
      }
      const moduleRes = await dispatch(
        fetch(`/courses/${courseRes.data.id}/modules`, {
          type: "GET"
        })
      )
      preconnectModuleImageOrigins(moduleRes.data)
      return { course: courseRes.data, modules: moduleRes.data }
    } catch (error) {
      console.error(
        "[fetchCourseWithModules] Failed to fetch course:",
        courseNameOrId,
        error?.message
      )
      return { course: null, modules: [] }
    }
  }
}

export const navigateToWorkshop = (
  history,
  match,
  courseName = "Pursue Your Purpose",
  courseId = null,
  targetModuleId = null
) => {
  return async (dispatch, getState) => {
    try {
      const { pyp, user } = getState()
      const courseRes = courseId
        ? await dispatch(
            fetch(`/courses/${courseId}`, {
              type: "GET"
            })
          )
        : await dispatch(
            fetch(`/courses`, {
              type: "GET",
              data: {
                params: {
                  name: courseName
                }
              }
            })
          )

      const resolvedCourseName = courseRes.data?.name || courseName
      const resolvedCourseId = courseId || courseRes.data?.id

      const moduleRes = await dispatch(
        fetch(`/courses/${courseRes.data.id}/modules`, {
          type: "GET"
        })
      )
      preconnectModuleImageOrigins(moduleRes.data)

      const courseData = courseRes.data
      courseData.modules = moduleRes.data

      let moduleToNavigateTo
      const modules = moduleRes.data || []
      const isCourseBuilderCourse =
        Number(courseId) === ADDITIONAL_COURSE_ID

      if (isCourseBuilderCourse) {
        // Course builder: prefer explicit target, else first incomplete in course order.
        if (targetModuleId != null) {
          moduleToNavigateTo = modules.find(
            (module) => Number(module.id) === Number(targetModuleId)
          )
        }
        if (!moduleToNavigateTo) {
          const enrolled =
            user?.user?.all_enrolled_modules || []
          moduleToNavigateTo = modules.find((module) => {
            const progress = enrolled.find(
              (um) => Number(um.id) === Number(module.id)
            )
            return !progress || Number(progress?.pivot?.completed) !== 1
          })
        }
        if (!moduleToNavigateTo) {
          moduleToNavigateTo = modules[0]
        }
      } else {
        // PYP / other: existing behavior only
        if (pyp.navigation.currentModuleId > -1) {
          moduleToNavigateTo = modules.find((module) => {
            return module.id === pyp.navigation.currentModuleId
          })
        }
        if (!moduleToNavigateTo) {
          moduleToNavigateTo = modules[0]
        }
      }

      history.push({
        ...internals.buildWorkshopLocation(
          moduleToNavigateTo.title,
          {
            currentCourseId: courseId ? resolvedCourseId : -1,
            currentCourseName: resolvedCourseName
          }
        ),
        state: {
          courseName: resolvedCourseName,
          courseId: courseId ? resolvedCourseId : undefined
        }
      })
    } catch (error) {
      dispatch({
        type: types.EXCEPTION,
        data: error
      })
    }
  }
}

export const decrementPartId = (history) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()

      const { currentPartId, currentModuleId } = pyp.navigation
      const currentPartIndex = getCurrentPartIndex(pyp.parts, currentPartId)
      const previousIndex = getPreviousPartIndex(currentPartIndex)
      const moduleIndex = getCurrentModuleIndex(pyp.modules, currentModuleId)

      if (previousIndex === -1) {
        history.push(
          buildWorkshopLocation(
            pyp.modules.byId[pyp.modules.allIds[moduleIndex]].title,
            pyp.navigation
          )
        )
      } else {
        // const previousPart = pyp.parts.byId[pyp.parts.allIds[previousIndex]];
        // history.push(
        // `/workshop/${pyp.modules.byId[pyp.modules.allIds[moduleIndex]].title}/content?what=prev`
        // );
      }

      dispatch({
        type: types.DECREMENT_PART,
        data: {
          partId: pyp.parts.allIds[previousIndex],
          moduleId: pyp.modules.allIds[moduleIndex]
        }
      })
    } catch (data) {
      dispatch({
        type: types.EXCEPTION,
        data: data.response
      })
    }
  }
}

export const beginModule = (history) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentModuleId } = pyp.navigation

      history.push(
        internals.buildWorkshopLocation(
          pyp.modules.byId[currentModuleId].title,
          pyp.navigation,
          "/content"
        )
      )

      // TOOD: only if started isn't set to true

      const currentUnlockedUserModuleIdModule = pyp.unlockedUserModules.find(
        (unlockedModule) => {
          return (
            Number(unlockedModule.module_id) === Number(currentModuleId)
          )
        }
      ).id

      const userModules = await dispatch(
        fetch(`/userModules`, {
          type: "GET"
        })
      )

      const currentModule = userModules.data.filter((module) => {
        return Number(module.module_id) === Number(currentModuleId)
      })[0]

      const moduleAlreadyCompleted = currentModule.completed === 1
      const moduleAlreadyStarted = currentModule.started === 1

      // window.dataLayer.push({
      //   pypModule: pyp.modules.byId[currentModule.module_id].title
      // })

      if (!moduleAlreadyStarted) {
        await dispatch(
          fetch(`/userModules/${currentUnlockedUserModuleIdModule}`, {
            type: "PUT",
            data: {
              requestPayload: {
                started: true
              }
            }
          })
        )

        const updatedUnlockedUserModules = await dispatch(
          fetch(`/userModules`, {
            type: "GET"
          })
        )

        const userRes = await dispatch(
          fetch(`/user`, {
            type: "GET"
          })
        )

        dispatch({
          type: types.FETCHED_USER,
          user: userRes.data[0]
        })

        try {
          dispatch({
            type: types.BEGIN_MODULE,
            data: {
              unlockedUserModules: updatedUnlockedUserModules.data,
              module: pyp.modules.byId[currentModule.module_id]
            }
          })
        } catch (error) {
          dispatch({
            type: types.EXCEPTION,
            data: error.response
          })
        }
      } else if (!moduleAlreadyCompleted) {
        // these two dispatches are for google analytics and not for redux
        dispatch({
          type: types.RESUME_MODULE,
          data: {
            module: pyp.modules.byId[currentModule.module_id]
          }
        })
      } else {
        dispatch({
          type: types.UPDATE_MODULE,
          data: {
            module: pyp.modules.byId[currentModule.module_id]
          }
        })
      }
    } catch (error) {
      dispatch({
        type: types.EXCEPTION,
        data: error.response
      })
      // history.push('/');
    }
  }
}

const checkIfConstraintsAreFilled = () => (partData) => {
  const { constraints } = partData
  return !!(constraints && constraints[0] && constraints[1])
}

const getButtonValuesToUpdate = (partData) => {
  if (!partData?.valuesToUpdate) return null
  if (Array.isArray(partData.valuesToUpdate)) return partData.valuesToUpdate
  if (Array.isArray(partData.valuesToUpdate.value)) return partData.valuesToUpdate.value
  return null
}

const getButtonSelectedItems = (partData) => {
  if (partData?.selectedItems && Array.isArray(partData.selectedItems)) {
    return partData.selectedItems
  }
  if (
    partData?.valuesToUpdate?.selectedItems &&
    Array.isArray(partData.valuesToUpdate.selectedItems)
  ) {
    return partData.valuesToUpdate.selectedItems
  }
  return null
}

const checkIfButtonSelectionFilled = (defaultLimit, stage) => (
  partData,
  options = {}
) => {
  // Guard against undefined or null partData
  if (!partData) {
    return false
  }

  // Guard against undefined or null buttonValues
  if (!partData.buttonValues || !Array.isArray(partData.buttonValues)) {
    return false
  }

  const optionCount = partData.buttonValues.length
  const titleMeta = Array.isArray(partData.title)
    ? partData.title[0]
    : partData.title
  const isCourseBuilder = !!options.isCourseBuilder && !stage

  const valuesArray = getButtonValuesToUpdate(partData)
  const selectedItems = getButtonSelectedItems(partData)

  const buttonSelections = partData.buttonValues.filter((buttonValue) => {
    // Guard against undefined buttonValue or buttonSelections
    if (!buttonValue || !buttonValue.buttonSelections || !Array.isArray(buttonValue.buttonSelections)) {
      return false
    }

    if (valuesArray) {
      return (
        buttonValue.buttonSelections.length > 0 &&
        (stage ? (buttonValue.buttonSelections[0]?.pivot?.stage === 2) : true) &&
        !valuesArray.find((value) => {
          return value.id === buttonValue.id && !value.isSelected
        })
      )
    }
    return (
      buttonValue.buttonSelections.length > 0 &&
      (stage ? (buttonValue.buttonSelections[0]?.pivot?.stage === 2) : true)
    )
  })

  // Course Builder: Excel selectionLimit, count saved clicks, never wedge on valuesToUpdate.
  if (isCourseBuilder) {
    const persistedCount = countPersistedButtonSelections(partData.buttonValues, {
      stage,
      valuesToUpdate: valuesArray
    })
    const inMemoryCount =
      selectedItems && Array.isArray(selectedItems) ? selectedItems.length : 0
    const pendingNewSelects = (valuesArray || []).filter((value) => {
      if (!value?.isSelected) return false
      const existing = partData.buttonValues.find((btn) => btn.id === value.id)
      const alreadyCounted =
        existing &&
        Array.isArray(existing.buttonSelections) &&
        existing.buttonSelections.length > 0
      return !alreadyCounted
    }).length
    const selectedCount = Math.max(
      persistedCount + pendingNewSelects,
      inMemoryCount,
      buttonSelections.length
    )

    return isButtonSelectionRequirementMet({
      isCourseBuilder: true,
      titleMeta,
      optionCount,
      selectedCount,
      stage,
      stageDefaultLimit: defaultLimit
    })
  }

  const fromServer = Number(
    titleMeta?.selection_limit ?? titleMeta?.selectionLimit
  )
  const limit = stage
    ? defaultLimit
    : Number.isFinite(fromServer) && fromServer > 0
      ? fromServer
      : optionCount >= 10
        ? 10
        : 3

  if (valuesArray || selectedItems) {
    if (!selectedItems || !Array.isArray(selectedItems)) {
      return false
    }
    return selectedItems.length >= limit
  }

  return buttonSelections.length >= limit
}

const checkIfInputFilled = () => (partData) => {
  if (!partData || !partData.inputs || !Array.isArray(partData.inputs)) {
    return false
  }
  
  // SalesforcePyp derives the "*_DT" values automatically when the corresponding main input is saved.
  // So treat inputs whose UI label includes "DT" as optional for completion gating.
  const isOptionalDtInput = (input) => {
    const title =
      (input?.title?.title ?? input?.title ?? "").toString?.() ??
      ""
    return /\bDT\b/i.test(title) || title.toLowerCase().endsWith(" dt");
  }

  const realInputs = partData.inputs.filter(
    (input) =>
      input &&
      !(typeof input.id === "string" && input.id.startsWith("temp_"))
  )
  if (!realInputs.length) {
    return false
  }

  const unfilledInput = realInputs.find((input) => {
    if (isOptionalDtInput(input)) return false
    const text = input.text
    return !text || !String(text).trim().length
  })

  if (unfilledInput) {
    return false
  }
  return true
}

const checkIfLeadSelectionComplete = () => (partData) => {
  // Guard against undefined or null partData
  if (!partData) {
    return false
  }
  
  // Guard against undefined or null referenceInputs
  if (!partData.referenceInputs || !Array.isArray(partData.referenceInputs)) {
    return false
  }
  
  const indexOfLead = partData.referenceInputs.findIndex(
    (input) => input && input.buttonSelectionTitle && input.buttonSelectionTitle.is_lead
  )
  if (indexOfLead < 0 || !partData.referenceInputs.length) {
    return false
  }
  return true
}

const fillCheckerMap = {
  "User Input Small": checkIfInputFilled(),
  "User Input Medium": checkIfInputFilled(),
  "User Input Large": checkIfInputFilled(),
  "Reference Lead Selection Small": checkIfLeadSelectionComplete(),
  "Reference Lead Selection Medium": checkIfLeadSelectionComplete(),
  "Reference Lead Selection Large": checkIfLeadSelectionComplete(),
  Constraints: checkIfConstraintsAreFilled(),
  "Button Selection": checkIfButtonSelectionFilled(10),
  "Button Selection Stage 2": checkIfButtonSelectionFilled(3, 2),
  default: () => {
    // Invalid argument
  }
}

const partIsComplete = (partElements, partData, isCourseBuilder = false) => {
  // Guard against undefined or null partElements
  if (!partElements || !Array.isArray(partElements)) {
    return false
  }
  
  // Guard against undefined or null partData
  if (!partData) {
    return false
  }
  
  const needToBeFilledElements = [
    "User Input Small",
    "User Input Medium",
    "User Input Large",
    "Button Selection",
    "Button Selection Stage 2",
    "Reference Lead Selection Small",
    "Reference Lead Selection Medium",
    "Reference Lead Selection Large",
    "Constraints"
  ]

  const elementsThatNeedFilling = partElements.filter((element) =>
    element && element.name && needToBeFilledElements.includes(element.name)
  )
  if (elementsThatNeedFilling.length) {
    return elementsThatNeedFilling.reduce((isFilled, element) => {
      if (!isFilled) {
        return false
      }
      const fillCheckerFn =
        fillCheckerMap[element.name] || fillCheckerMap.default
      return fillCheckerFn(partData, { isCourseBuilder })
    }, true)
  }
  return true
}

export const initializePYPPart = (match, history, retryCount = 0) => {
  return async (dispatch, getState) => {
    const maxInitRetries = 100
    try {
      console.log('[initializePYPPart] Starting initialization', {
        module: match.params.module,
        pathname: match.path
      })

      dispatch({
        type: types.INITIALIZING_PART
      })
      const { pyp } = getState()
      
      console.log('[initializePYPPart] State check', {
        pypInitialized: pyp.initialized,
        hasModules: !!(pyp.modules && pyp.modules.byId),
        modulesCount: pyp.modules?.allIds?.length || 0,
        hasNavigation: !!pyp.navigation,
        currentModuleId: pyp.navigation?.currentModuleId,
        hasCourses: !!(pyp.courses && pyp.courses.byId),
        coursesCount: pyp.courses?.allIds?.length || 0
      })
      
      // Safety check: Ensure PYP is initialized and module data is ready
      if (!pyp.initialized || !pyp.modules || !pyp.modules.byId || !pyp.navigation) {
        if (retryCount >= maxInitRetries) {
          console.error("[initializePYPPart] PYP failed to initialize after retries")
          return
        }
        console.log("[initializePYPPart] PYP not ready, retrying...")
        setTimeout(() => {
          dispatch(initializePYPPart(match, history, retryCount + 1))
        }, 100)
        return
      }
      
      // Safety check: Ensure currentModuleId is valid
      if (pyp.navigation.currentModuleId === -1 || !pyp.navigation.currentModuleId) {
        console.log('[initializePYPPart] currentModuleId is -1, trying to match from URL')
        // Try to match module from URL
        const moduleTitle = decodeURIComponent(match.params.module)
        console.log('[initializePYPPart] Looking for module:', moduleTitle)
        const matchedModule = Object.values(pyp.modules.byId).find((module) => {
          return module.title === moduleTitle
        })
        
        if (matchedModule) {
          console.log('[initializePYPPart] Found module, navigating to:', matchedModule.id)
          dispatch({
            type: types.NAVIGATE_TO_MODULE,
            data: {
              moduleId: matchedModule.id
            }
          })
          // Retry after state update
          setTimeout(() => {
            dispatch(initializePYPPart(match, history))
          }, 50)
          return
        } else {
          console.error('[initializePYPPart] Module not found:', moduleTitle, 'Available modules:', Object.values(pyp.modules.byId).map(m => m.title))
          dispatch({
            type: types.INITIALIZED_PART,
            data: {
              parts: { allIds: [], byId: {} },
              partData: { allIds: [], byId: {} },
              elements: {},
              navigation: {
              currentPartId: -1
              },
              currentPartComplete: false
            }
          })
          return
        }
      }
      
      const { courses } = pyp
      
      // Safety check: Ensure courses data is ready
      if (!courses || !courses.byId || !courses.allIds || courses.allIds.length === 0) {
        if (retryCount >= maxInitRetries) {
          console.error("[initializePYPPart] Courses not ready after retries")
          return
        }
        console.log("[initializePYPPart] Courses not ready, retrying...")
        setTimeout(() => {
          dispatch(initializePYPPart(match, history, retryCount + 1))
        }, 100)
        return
      }
      
      const currentModule = pyp.modules.byId[pyp.navigation.currentModuleId]
      
      // Safety check: Ensure currentModule exists
      if (!currentModule) {
        console.error('[initializePYPPart] Current module not found:', pyp.navigation.currentModuleId)
        dispatch({
          type: types.INITIALIZED_PART,
          data: {
            parts: { allIds: [], byId: {} },
            partData: { allIds: [], byId: {} },
            elements: {},
            navigation: {
            currentPartId: -1
            },
            currentPartComplete: false
          }
        })
        return
      }

      const activeCourseId = internals.resolveActiveCourseId(pyp)
      if (!activeCourseId) {
        console.error("[initializePYPPart] No active course id in PYP state")
        dispatch({
          type: types.INITIALIZED_PART,
          data: {
            parts: { allIds: [], byId: {} },
            partData: { allIds: [], byId: {} },
            elements: {},
            navigation: { currentPartId: -1 },
            currentPartComplete: false
          }
        })
        return
      }

      console.log(
        "[initializePYPPart] Fetching parts for module:",
        currentModule.id,
        "course:",
        activeCourseId
      )
      const partsRes = await dispatch(
        fetch(
          `/courses/${activeCourseId}/modules/${currentModule.id}/parts`,
          {
            type: "GET"
          }
        )
      )
      console.log('[initializePYPPart] Parts fetched:', partsRes.data?.length || 0, 'parts')

      if (!partsRes.data || !partsRes.data.length) {
        console.warn(
          "[initializePYPPart] No parts for course=",
          activeCourseId,
          "module=",
          currentModule.id,
          "— GET /courses/" +
            activeCourseId +
            "/modules/" +
            currentModule.id +
            "/parts returned empty; check module_parts in DB or Course Builder upload"
        )
        dispatch({
          type: types.INITIALIZED_PART,
          data: {
            parts: { allIds: [], byId: {} },
            partData: { allIds: [], byId: {} },
            elements: {},
            navigation: { currentPartId: -1 },
            currentPartComplete: false
          }
        })
        return
      }

      // Get from the last visited part associated with the currentModule
      // If moduleNavigationMetaData doesn't exist or doesn't have this module, default to -1
      const moduleNavMeta = pyp.navigation.moduleNavigationMetaData?.[currentModule.id]
      let partId = moduleNavMeta?.lastVisitedPart ?? -1
      const partIdsInModule = (partsRes.data || []).map((p) => p.id)
      let correctedModuleNavigationMetaData = null

      if (
        internals.resolveActiveCourseId(pyp) === ADDITIONAL_COURSE_ID &&
        partId > 0 &&
        !partIdsInModule.includes(partId)
      ) {
        partId = partsRes.data[0]?.id ?? -1
        if (partId > 0) {
          correctedModuleNavigationMetaData = {
            ...pyp.navigation.moduleNavigationMetaData,
            [currentModule.id]: {
              id: currentModule.id,
              lastVisitedPart: partId
            }
          }
        }
      }
      
      console.log('[initializePYPPart] Using partId:', partId, 'from moduleNavigationMetaData')

      let part = partsRes.data.find((part) => {
        return part.id === partId
      })

      // user hit back button, exited module, etc.
      if (!part) {
        part = partsRes.data[0]
      }

      // Generate PYP element string for mapping to api call
      const elementString = internals.getElementString(part.elements, pyp)
      console.log('[initializePYPPart] Part element string:', elementString, 'for part:', part.id, part.name)

      const apiCall = apiMap[elementString] || apiMap.default
      if (!apiCall) {
        console.error('[initializePYPPart] No API call found for element string:', elementString)
      }
      console.log('[initializePYPPart] Fetching part data for part:', part.id)
      const data = await apiCall(dispatch, part.id)
      console.log('[initializePYPPart] Part data fetched:', !!data)

      // Create a copy of the part object instead of mutating it directly
      const partWithData = { ...part, partData: data }

      const normalizedData = normalize(
        {
          parts: partsRes.data.map(p => p.id === part.id ? partWithData : p),
          partData: [data],
          elements: partsRes.data[0].elements
        },
        {
          parts: [partSchema],
          partData: [partDataSchema],
          elements: [elementSchema]
        }
      )

      if (
        !pyp.unlockedUserModules.find((unlockedModule) => {
          return (
            Number(unlockedModule.module_id) ===
            Number(pyp.navigation.currentModuleId)
          )
        })
      ) {
        history.push(
          buildWorkshopLocation(match.params.module, pyp.navigation)
        )
      }

      console.log('[initializePYPPart] Dispatching INITIALIZED_PART', {
        partId: part.id,
        partsCount: normalizedData.result.parts?.length || 0,
        partDataCount: normalizedData.result.partData?.length || 0,
        elementsCount: Object.keys(normalizedData.entities.elements || {}).length
      })

      dispatch({
        type: types.INITIALIZED_PART,
        data: {
          parts: {
            allIds: normalizedData.result.parts,
            byId: normalizedData.entities.parts
          },
          partData: {
            allIds: normalizedData.result.partData,
            byId: normalizedData.entities.partData
          },
          elements: { byId: normalizedData.entities.elements },
          navigation: {
            currentPartId: part.id,
            ...(correctedModuleNavigationMetaData
              ? { moduleNavigationMetaData: correctedModuleNavigationMetaData }
              : {})
          },
          currentPartComplete: partIsComplete(
            part && part.elements && Array.isArray(part.elements)
              ? part.elements.map((elementId) => normalizedData.entities.elements?.[elementId]).filter(Boolean)
              : [],
            data.data,
            isCourseBuilderWorkshop(pyp)
          )
        }
      })
      console.log('[initializePYPPart] Initialization complete')
    } catch (error) {
      console.error('[initializePYPPart] Error during initialization:', error)
      console.error('[initializePYPPart] Error stack:', error.stack)
      // Error handling - ensure structure matches reducer expectations
      dispatch({
        type: types.INITIALIZED_PART,
        data: {
          parts: { allIds: [], byId: {} },
          partData: { allIds: [], byId: {} },
          elements: {},
          navigation: {
            currentPartId: -1
          },
          currentPartComplete: false
        }
      })
    }
  }
}

const getInputSize = (input) => {
  const inputMap = {
    "User Input Small": "small",
    "User Input Large": "large",
    "User Input Medium": "medium"
  }

  return inputMap[input.name]
}

// Called when a PYP part is mounted. Creates new inputs within the database
export const initializePYPInputs = (partDataId, inputTitles) => {
  return async (dispatch, getState) => {
    try {
      dispatch({
        type: types.INITIALIZING_PYP_INPUTS
      })
      const { pyp } = getState()
      const currentPart = pyp.parts.byId[pyp.navigation.currentPartId]
      const { elements } = currentPart
      const inputsToMake = elements
        .map((elementId) => pyp.elements.byId[elementId])
        .filter((element) => element.name.includes("User Input"))

      // Create one user_inputs row per input title (Course Builder: 1 element, N labels)
      const userInputInsertions = inputTitles.map(async (title, i) => {
        const element = inputsToMake[i] || inputsToMake[0]
        const size = element ? getInputSize(element) : "small"
        return dispatch(
          fetch(`/userInputs`, {
            type: "POST",
            data: {
              requestPayload: {
                partId: currentPart.id,
                titleId: title.id,
                size
              }
            }
          })
        )
      })

      const inputsRes = await Promise.all(userInputInsertions)

      // Re-read after awaits so temp_/typed text from during create is not lost
      const pypAfterCreate = getState().pyp
      const currentPartData =
        pypAfterCreate.partData.byId[currentPart.partData]
      const existingData = currentPartData?.data || {}
      const rawInputs = existingData.inputs || []
      const tempByTitleId = {}
      rawInputs.forEach((row) => {
        if (typeof row.id === "string" && row.id.startsWith("temp_")) {
          tempByTitleId[String(row.id.replace("temp_", ""))] = row.text
        }
      })
      const existingInputs = rawInputs.filter(
        (row) => row.id && !String(row.id).startsWith("temp_")
      )
      const allTitles = existingData.inputTitle || inputTitles

      const mergedByTitleId = new Map()
      for (const row of existingInputs) {
        const tid = Number(row.input_title_id ?? row.inputTitleId)
        if (!Number.isNaN(tid)) mergedByTitleId.set(tid, row)
      }
      inputsRes.forEach((input) => {
        const data = input.data
        const tid = Number(data.input_title_id ?? data.inputTitleId)
        const prev = mergedByTitleId.get(tid)
        const tempText = tempByTitleId[String(tid)]
        mergedByTitleId.set(tid, {
          ...data,
          text: internals.pickPreferredInputText(
            tempText,
            prev?.text,
            data.text
          )
        })
      })

      const mergedInputs = allTitles
        .map((title) => mergedByTitleId.get(Number(title.id)))
        .filter(Boolean)

      const partData = {
        ...pypAfterCreate.partData,
        byId: {
          ...pypAfterCreate.partData.byId,
          [currentPartData.id]: {
            ...pypAfterCreate.partData.byId[currentPartData.id],
            data: {
              ...pypAfterCreate.partData.byId[currentPartData.id].data,
              inputs:
                mergedInputs.length > 0
                  ? mergedInputs
                  : inputsRes.map((input) => input.data)
            }
          }
        }
      }

      const currentElements = currentPart.elements
        .map((elementId) => pypAfterCreate.elements.byId[elementId])
        .filter(Boolean)

      dispatch({
        type: types.CREATE_PYP_INPUTS,
        data: {
          partDataId,
          partData,
          currentPartComplete: partIsComplete(
            currentElements,
            partData.byId[currentPartData.id].data,
            isCourseBuilderWorkshop(pypAfterCreate)
          )
        }
      })
    } catch (error) {
      dispatch({ type: types.FAILED_PYP_INPUTS_INIT })
      const { pyp } = getState()
      if (isCourseBuilderWorkshop(pyp)) {
        dispatch({
          type: types.EXCEPTION,
          data: error?.response || { message: error?.message, error }
        })
      }
    }
  }
}

// Called when a PYP part is mounted. Creates new inputs within the database
export const updatePYPInputsFromButton = (
  partDataId,
  partId,
  selectedButtons,
  inputBoxes
) => {
  return async (dispatch, getState) => {
    try {
      dispatch({
        type: types.SAVING_PYP
      })
      // TODO: For each button that was deselected in the previous part, DELETE the cooresponding input box
      // ~ For every inputBox that EXISTS ALREADY, where its button_value_id does NOT EXIST in selectedButtons, DELETE IT

      for (const input of inputBoxes) {
        if (
          !selectedButtons.find((btn) => {
            return (
              (input.buttonSelectionTitle &&
                input.buttonSelectionTitle.buttonValue &&
                input.buttonSelectionTitle.button_value_id &&
                input.buttonSelectionTitle.buttonValue.id ===
                  btn.buttonSelections[0].pivot.button_value_id) ||
              /*
 Custom Button Values are currently unused. It was a feature that was approved, but never released due to client concerns.
 It's possible it might make it back in, so we didn't remove the code.
*/
              (input.buttonSelectionTitle &&
                input.buttonSelectionTitle.customButtonValue &&
                input.buttonSelectionTitle.custom_button_value_id &&
                input.buttonSelectionTitle.customButtonValue.id ===
                  btn.buttonSelections[0].pivot.custom_button_value_id)
            )
          })
        ) {
          // Delete that input
          await dispatch(
            fetch(`/userInputs/${input.id}`, {
              type: "DELETE"
            })
          )

          // TODO: Update the store to reflect these deletions (which should update the UI)
        }
      }

      // Creates a new input box for each selectd button that does not already have a cooresponding input box
      // ~ For every selectedButton where its id does NOT ALREADY EXIST as a button_value_id in inputBoxes, POST to create a new inputBox
      const inputs = []
      //"/buttonValues/SalesforceValues"


      let sortedSelectedButtons = [];
      if(partId == 22){
        const valuesRes = await dispatch(
          fetch(`/salesforceValues`, {
            type: "GET"
          })
        )
        const values = valuesRes?.data

        for (const [index, btn] of selectedButtons.entries()) {
          if(btn.name != values.Value_1__c && btn.name != values.Value_2__c && btn.name != values.Value_3__c){
            sortedSelectedButtons.push(btn)
          }
        }

        for (const [index, btn] of selectedButtons.entries()) {
          if(btn.name == values.Value_1__c){
            sortedSelectedButtons.splice( 0, 0, btn );
          }
          else if(btn.name == values.Value_2__c){
            sortedSelectedButtons.splice( 1, 0, btn );
          }
          else if(btn.name == values.Value_3__c){
            sortedSelectedButtons.splice( 2, 0, btn );
          }

        }

        /*const filteredSelectedButtons = sortedSelectedButtons.filter((btn, index)=>{
          return !!btn
        })*/
      }
      else if (partId == 28){
        const cypRes = await dispatch(
          fetch(`/salesforceCheckYourPack`, {
            type: "GET"
          })
        )
        const cyp = cypRes?.data

        for (const [index, btn] of selectedButtons.entries()) {
          if(btn.name != cyp.Skill_1__c && btn.name != cyp.Skill_2__c && btn.name != cyp.Skill_3__c){
            sortedSelectedButtons.push(btn)
          }
        }

        for (const [index, btn] of selectedButtons.entries()) {
          if(btn.name == cyp.Skill_1__c){
            sortedSelectedButtons.splice( 0, 0, btn );
          }
          else if(btn.name == cyp.Skill_2__c){
            sortedSelectedButtons.splice( 1, 0, btn );
          }
          else if(btn.name == cyp.Skill_3__c){
            sortedSelectedButtons.splice( 2, 0, btn );
          }

        }

        /*const filteredSelectedButtons = sortedSelectedButtons.filter((btn, index)=>{
          return !!btn
        })*/
      }
      else{
        sortedSelectedButtons  = selectedButtons.sort((a, b) =>
          a.name > b.name ? 1 : -1
        )
      }

      for (const [index, btn] of sortedSelectedButtons.entries()) {
        if (
          !inputBoxes.find((input) => {
            return (
              (input.buttonSelectionTitle &&
                input.buttonSelectionTitle.buttonValue &&
                input.buttonSelectionTitle.button_value_id &&
                input.buttonSelectionTitle.buttonValue.id ===
                  btn.buttonSelections[0].pivot.button_value_id) ||
              /*
 Custom Button Values are currently unused. It was a feature that was approved, but never released due to client concerns.
 It's possible it might make it back in, so we didn't remove the code.
*/
              (input.buttonSelectionTitle &&
                input.buttonSelectionTitle.customButtonValue &&
                input.buttonSelectionTitle.custom_button_value_id &&
                input.buttonSelectionTitle.customButtonValue.id ===
                  btn.buttonSelections[0].pivot.custom_button_value_id)
            )
          })
        ) {
          const res = await dispatch(
            fetch(`/userInputs`, {
              type: "POST",
              data: {
                requestPayload: {
                  partId,
                  buttonId: btn.buttonSelections[0].pivot.button_value_id,
                  customButtonId:
                    btn.buttonSelections[0].pivot.custom_button_value_id,
                  inputPosition: index + 1
                }
              }
            })
          )
          inputs.push(res.data)
        }
      }

      const updatedInputs = await dispatch(
        fetch(`/userInputs`, {
          type: "GET",
          data: {
            params: {
              partId
            }
          }
        })
      )

      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      const currentPart = pyp.parts.byId[currentPartId]
      const currentPartData = pyp.partData.byId[currentPart.partData]
      const currentElements = currentPart.elements.map((elementId) => {
        return pyp.elements.byId[elementId]
      })

      const partData = {
        ...pyp.partData,
        byId: {
          ...pyp.partData.byId,
          [currentPartData.id]: {
            ...pyp.partData.byId[currentPartData.id],
            data: {
              ...pyp.partData.byId[currentPartData.id].data,
              inputs: updatedInputs.data
            }
          }
        }
      }

      // This PUSHES the newly created inputs into the inputs field of the reducer
      dispatch({
        type: types.CREATE_PYP_INPUTS,
        data: {
          partData,
          currentPartComplete: partIsComplete(
            currentElements,
            partData.byId[currentPartData.id].data,
            isCourseBuilderWorkshop(pyp)
          )
        }
      })

      dispatch({
        type: types.SAVED_PYP
      })
    } catch (error) {
      // Error handling
    }
  }
}

export const createButton = (name, partId, userId) => {
  return async (dispatch, getState) => {
    try {
      /*
 Custom Button Values are currently unused. It was a feature that was approved, but never released due to client concerns.
 It's possible it might make it back in, so we didn't remove the code.
*/
      // API call to create a new button
      const res = await dispatch(
        fetch(`/customButtonValues`, {
          type: "POST",
          data: { requestPayload: { name, partId } }
        })
      )

      // Add buttonSelections and isCustom properties to the button,
      // since these properties are not returned after the POST and would require re-fetching all buttons
      const newCustomButton = res.data
      newCustomButton.buttonSelections = []
      newCustomButton.isCustom = true

      dispatch({
        type: types.INSERT_CUSTOM_BUTTON,
        data: {
          newButton: newCustomButton
        }
      })
    } catch (error) {
      // Error handling
    }
  }
}

/**
 * Apply multiple input text updates in one Redux write (avoids lost updates from
 * concurrent setPYPInputText calls).
 * patches: [{ id, text, removeTempTitleId? }]
 */
export const applyInputTextPatches = (partDataId, patches) => {
  return (dispatch, getState) => {
    try {
      if (!patches || !patches.length) return
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      const currentPart = pyp.parts?.byId?.[currentPartId]
      const resolvedPartDataId = partDataId || currentPart?.partData
      const currentPartData =
        resolvedPartDataId && pyp.partData?.byId?.[resolvedPartDataId]

      if (!currentPartData || !currentPartData.id) {
        console.error(
          "[applyInputTextPatches] currentPartData is missing or invalid:",
          { currentPartData, currentPartId, partDataId }
        )
        return
      }

      const currentElements =
        currentPart && currentPart.elements && Array.isArray(currentPart.elements)
          ? currentPart.elements
              .map((elementId) => pyp.elements?.byId?.[elementId])
              .filter((element) => element !== undefined && element !== null)
          : []

      let newInputs = [...(currentPartData.data?.inputs || [])]
      const tempTitleIdsToRemove = new Set()

      for (const patch of patches) {
        if (!patch || patch.id == null) continue
        const matchesId = (row) =>
          row &&
          (row.id === patch.id || String(row.id) === String(patch.id))
        if (newInputs.some(matchesId)) {
          newInputs = newInputs.map((input) =>
            matchesId(input) ? { ...input, text: patch.text } : input
          )
        } else {
          newInputs = [...newInputs, { id: patch.id, text: patch.text }]
        }
        if (patch.removeTempTitleId != null) {
          tempTitleIdsToRemove.add(String(patch.removeTempTitleId))
        }
      }

      if (tempTitleIdsToRemove.size > 0) {
        newInputs = newInputs.filter((row) => {
          if (typeof row.id !== "string" || !row.id.startsWith("temp_")) {
            return true
          }
          return !tempTitleIdsToRemove.has(row.id.replace("temp_", ""))
        })
      }

      const built = internals.buildPartDataWithInputs(
        pyp,
        currentPartData.id,
        newInputs,
        currentElements
      )
      if (!built) return

      dispatch({
        type: types.SET_PYP_INPUT_DATA,
        data: built
      })
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response?.data || response?.message || response
        }
      })
    }
  }
}

/** Sync thunk — sequential dispatches see each other's updates (no async race). */
export const setPYPInputText = (partDataId, inputId, value) => {
  return (dispatch) => {
    dispatch(
      applyInputTextPatches(partDataId, [{ id: inputId, text: value }])
    )
  }
}

// const saveButtonSelection = async (apiToken, buttonValueId) => {
//   const res = await axios.post(
//     `/userButtonValues/`,
//     {
//       buttonValueId
//     },
//     { headers: { Authorization: apiToken } }
//   );

//   return res;
// };

const saveApiMap = {
  "Button Selection,Meta Data": async (dispatch, partData, partId) => {
    try {
      const valuesToUpdate = getButtonValuesToUpdate(partData)

      const insertions =
        valuesToUpdate &&
        valuesToUpdate
          .filter((value) => value.isSelected)
          .map((selection) => {
            return { id: selection.id, isCustom: selection.isCustom }
          })

      const deletions =
        valuesToUpdate &&
        valuesToUpdate
          .filter((value) => !value.isSelected)
          .map((selection) => {
            return { id: selection.id, isCustom: selection.isCustom }
          })

      insertions &&
        insertions.length &&
        (await dispatch(
          fetch(`/userButtonValues`, {
            type: "POST",
            data: {
              requestPayload: {
                buttonValues: insertions
              }
            }
          })
        ))

      deletions &&
        deletions.length &&
        (await dispatch(
          fetch(`/userButtonValues`, {
            type: "DELETE",
            data: {
              requestPayload: {
                buttonValues: deletions
              }
            }
          })
        ))

      await internals.syncUserInputsFromButtonSelections(
        dispatch,
        partId,
        insertions,
        deletions,
        partData
      )
    } catch (e) {
      // Error handling
    }
  },
  "Button Selection Stage 2,Meta Data": async (dispatch, partData, partId) => {
    // TODO: we could generalize this to 'upgrade' button selection
    // where we simply upgrade the current stage rather than hard code
    // 1 -> 2
    const valuesToUpdate = getButtonValuesToUpdate(partData)

    const updates =
      valuesToUpdate &&
      valuesToUpdate.map((selection) => {
        return {
          id: selection.id,
          isCustom: selection.isCustom,
          stage: selection.isSelected ? 2 : 1
        }
      })

    updates &&
      updates.length &&
      (await dispatch(
        fetch(`/userButtonValues`, {
          type: "PUT",
          data: {
            requestPayload: {
              userButtonValues: updates
            }
          }
        })
      ))
  },
  default: () => {}
}

/**
 * Part id that owns time_events / date_events + event_orders for PYP → Salesforce summary sync.
 * Matches loaders: "Meta Data,Roadmapper" and "Bargraph,Meta Data" use the third part_reference
 * (index 2) when length ≥ 3; otherwise the current part id.
 * Use when partData.eventOrder is missing (e.g. reflection / bargraph parts that show events from the roadmapmer part).
 */
async function resolveRoadmapSummaryPartId(dispatch, currentPartId, partData) {
  const fromOrder = partData.eventOrder && partData.eventOrder.part_id
  if (fromOrder !== undefined && fromOrder !== null && String(fromOrder).trim() !== "") {
    const n = Number(fromOrder)
    if (!isNaN(n) && n > 0) return n
  }
  try {
    const referencedPartRes = await dispatch(
      fetch(`/partReferences`, {
        type: "GET",
        data: {
          params: { partId: currentPartId }
        }
      })
    )
    const refs =
      referencedPartRes &&
      (Array.isArray(referencedPartRes.data)
        ? referencedPartRes.data
        : Array.isArray(referencedPartRes)
          ? referencedPartRes
          : [])
    if (refs && refs.length >= 3) {
      const third = refs[2]
      const pid =
        third?.partReferenceId ||
        third?.part_reference_id ||
        third?.part_referenceId ||
        third?.partReference_id
      if (pid !== undefined && pid !== null) {
        const n = Number(pid)
        if (!isNaN(n) && n > 0) return n
      }
    }
  } catch (e) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[savePYP] resolveRoadmapSummaryPartId partReferences failed", e)
    }
  }
  const fallback = Number(currentPartId)
  if (!isNaN(fallback) && fallback > 0) return fallback
  return null
}

// TODO: Currently this 'action creator' triggers 0 (that's right folks, ZERO) actions. Not sure how shameful such a thing is.
// it's the most shameful thing one can do in redux. I just puked thinking about it.

export const savePYP = () => {
  return async (dispatch, getState) => {
    try {
      const state = getState()
      // Exit savePYP function if data is still being fetched. There's nothing to save yet!
      if (state.pyp.fetchingPartData) {
        return
      }

      dispatch({ type: types.RESET_ERRORS })
      dispatch({
        type: types.SAVING_PYP
      })

      const currentElements =
        state.pyp.parts.byId[state.pyp.navigation.currentPartId].elements
      const elementString = internals.getElementString(
        currentElements,
        state.pyp
      )
      const currentPart =
        state.pyp.parts.byId[state.pyp.navigation.currentPartId]
      if (!currentPart.partData) {
        dispatch({ type: types.SAVED_PYP })
        return
      }
      const currentModuleTitle =
        state.pyp.modules.byId[state.pyp.navigation.currentModuleId]?.title
      const isInputGroup = [
        "Values",
        "Check Your Pack",
        "Courage vs. Confidence",
        "Finding the Right Fit"
      ].includes(currentModuleTitle)
      const currentPartId = state.pyp.navigation.currentPartId
      const partDataId = currentPart.partData
      const partData = state.pyp.partData.byId[partDataId].data
      if (process.env.NODE_ENV === 'development') {
        console.log('[savePYP] Start', {
          moduleTitle: currentModuleTitle,
          currentPartId,
          partDataId,
          hasInputs: !!(partData.inputs && partData.inputs.length),
          hasUserTimeEvents: !!(partData.userTimeEvents && partData.userTimeEvents.length),
          hasUserDateEvents: !!(partData.userDateEvents && partData.userDateEvents.length),
          hasButtonValues: !!partData.buttonValues
        })
      }
      const hasInputs = partData.inputs && partData.inputs.length
      const hasUserTimeEvents = partData.userTimeEvents && partData.userTimeEvents.length
      const hasUserDateEvents = partData.userDateEvents && partData.userDateEvents.length
      // If this part has text input boxes
      if (hasInputs) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[savePYP] Branch: inputs', {
            moduleTitle: currentModuleTitle,
            currentPartId,
            inputsCount: partData.inputs.length
          })
        }

        const getValuePosition = async (input, values, index) => {
            for (const [key, value] of Object.entries(values)) {
              if (value == input?.buttonSelectionTitle?.buttonValue?.name){
                if(key == 'Value_1__c'){
                  return 1
                }
                else if (key == 'Value_2__c'){
                  return 2
                }
                else if (key == 'Value_3__c'){
                  return 3
                }
              }
            }
            return index + 1

        }
        const getCypPosition = async (input, cyp, index) => {
          for (const [key, value] of Object.entries(cyp)) {
            if (value == input?.buttonSelectionTitle?.buttonValue?.name){
              if(key == 'Skill_1__c'){
                return 1
              }
              else if (key == 'Skill_2__c'){
                return 2
              }
              else if (key == 'Skill_3__c'){
                return 3
              }
            }
          }
          return index + 1

        }
        const getInputPosition = async (index) => {
          const currentModule =
            state.pyp.modules.byId[state.pyp.navigation.currentModuleId]

          /* Only courage vs confidence has a different behavior */
          if (currentModule?.title === "Courage vs. Confidence") {
            return {
              "Courage vs. Confidence 2.0 Inputs 1": 1,
              "Courage vs. Confidence 2.0 Inputs 2": 2,
              "Courage vs. Confidence 2.0 Inputs 3": 3
            }[currentPart.name]
          }

          return index + 1
        }
        /** We were using a Promise.all to fetch 3 requests at the same time,
         * and was probably causing conflicts in the data stored on salesforce,
         * so we decided to make 1 request at a time */
        let values
        let cyp
        if (state.pyp.modules.byId[state.pyp.navigation.currentModuleId].title === "Values") {
          const valuesRes = await dispatch(
            fetch(`/salesforceValues`, {
              type: "GET"
            })
          )
          values = valuesRes?.data
        } else if (state.pyp.modules.byId[state.pyp.navigation.currentModuleId].title === "Check Your Pack") {
          const cypRes = await dispatch(
            fetch(`/salesforceCheckYourPack`, {
              type: "GET"
            })
          )
          cyp = cypRes?.data
        }

        // Re-read after any awaits so temp_/pending text typed during init is included
        const freshPyp = getState().pyp
        const freshPartData =
          freshPyp.partData.byId[partDataId]?.data || partData
        const { mergedInputs, validTitleIds } =
          internals.prepareInputsForSave(freshPartData)

        for (let i = 0; i < mergedInputs.length; i++) {
          const input = mergedInputs[i]
          if (!input || input.id == null) {
            continue
          }
          // Never PUT against a temporary client-only id
          if (typeof input.id === "string" && String(input.id).startsWith("temp_")) {
            continue
          }
          const titleId = internals.getInputTitleId(input)
          const isButtonInput = !!(
            input.user_button_value_id ?? input.userButtonValueId
          )
          if (
            !isButtonInput &&
            validTitleIds.size > 0 &&
            (Number.isNaN(titleId) || !validTitleIds.has(titleId))
          ) {
            continue
          }
          const textToSave =
            input.text !== undefined && input.text !== null
              ? String(input.text)
              : ""
          try {
            await dispatch(
              fetch(`/userInputs/${input.id}`, {
                type: "PUT",
                data: {
                  requestPayload: {
                    text: textToSave,
                    inputPosition: state.pyp.modules.byId[state.pyp.navigation.currentModuleId].title === "Values" ? await getValuePosition(input, values, i) : 
                      state.pyp.modules.byId[state.pyp.navigation.currentModuleId].title === "Check Your Pack" ? await getCypPosition(input, cyp, i) : 
                        await getInputPosition(i),
                    isInputGroup
                  }
                }
              })
            )
          } catch (error) {
            // Error handling
          }
        }
      }
      // Run even when this part also has text inputs so Ideal Day Summary syncs to Salesforce.
      if (hasUserTimeEvents) {
        const partIdToUse = await resolveRoadmapSummaryPartId(
          dispatch,
          currentPartId,
          partData
        )
        if (process.env.NODE_ENV === 'development') {
          console.log('[savePYP] Branch: userTimeEvents -> timeEvents/updateAirtable', {
            moduleTitle: currentModuleTitle,
            currentPartId,
            partIdToUse,
            eventOrderPartId: partData.eventOrder?.part_id,
            timeEventsCount: partData.userTimeEvents.length
          })
        }

        if (!partIdToUse) {
          throw new Error('partId is required for timeEvents/updateAirtable')
        }

        await dispatch(
          fetch(`/timeEvents/updateAirtable`, {
            type: "POST",
            data: { requestPayload: { partId: partIdToUse } }
          })
        )
      }
      if (hasUserDateEvents) {
        const partIdToUse = await resolveRoadmapSummaryPartId(
          dispatch,
          currentPartId,
          partData
        )
        if (process.env.NODE_ENV === 'development') {
          console.log('[savePYP] Branch: userDateEvents -> dateEvents/updateAirtable', {
            moduleTitle: currentModuleTitle,
            currentPartId,
            partIdToUse,
            eventOrderPartId: partData.eventOrder?.part_id,
            dateEventsCount: partData.userDateEvents.length
          })
        }
        
        if (!partIdToUse) {
          throw new Error('partId is required for dateEvents/updateAirtable')
        }
        
        await dispatch(
          fetch(`/dateEvents/updateAirtable`, {
            type: "POST",
            data: { requestPayload: { partId: partIdToUse } }
          })
        )
      }
      // If this part has button values (mutually exclusive with input/time/date parts in legacy flow)
      if (!hasInputs && !hasUserTimeEvents && !hasUserDateEvents && partData.buttonValues) {
        const apiCall = saveApiMap[elementString] || saveApiMap.default
        await apiCall(dispatch, state.pyp.partData.byId[partDataId].data, currentPartId)
      }
      // If this part is a Reference Lead Selection Small/Medium/Large
      else if (
        !hasInputs &&
        !hasUserTimeEvents &&
        !hasUserDateEvents &&
        partData.referenceInputs &&
        elementString.includes("Reference Lead Selection")
      ) {
        // An array of user_button_values to update
        const updates = partData.referenceInputs.map((input) => {
          return {
            id:
              input.buttonSelectionTitle.button_value_id ||
              input.buttonSelectionTitle.custom_button_value_id,
            isCustom: !input.buttonSelectionTitle.button_value_id,
            is_lead: input.buttonSelectionTitle.is_lead
          }
        })

        // Make the API call
        updates &&
          updates.length &&
          (await dispatch(
            fetch(`/userButtonValues`, {
              type: "PUT",
              data: {
                requestPayload: {
                  userButtonValues: updates
                }
              }
            })
          ))
      }

      dispatch({
        type: types.SAVED_PYP
      })
    } catch (err) {
      dispatch({
        type: types.SAVED_PYP
      })
      console.error('[savePYP]', err?.response?.status, err?.response?.data || err?.message)
      // Do not use ERROR here: it sets errors.hasError and ErrorRenderer replaces PYP with FourOhFour.
      dispatch({
        type: types.EXCEPTION,
        data: err?.response || { message: err?.message, err }
      })
    }
  }
}

export const checkForPartCompletion = () => {
  return async (dispatch, getState) => {
    try {
      const state = getState()

      const currentPart =
        state.pyp.parts.byId[state.pyp.navigation.currentPartId]
      const partDataId = currentPart.partData
      const partData = state.pyp.partData.byId[partDataId].data

      if (partData.inputs && partData.inputs.length) {
        // If this part has text input boxes
        // Iterate through each input and check if a textbox has been left blank
        let incomplete = false
        for (const input of partData.inputs) {
          if (input.text.length <= 0) {
            incomplete = true
            break
          }
        }

        dispatch({
          type: types.UPDATE_PART_INCOMPLETION_ERROR,
          data: { showIncompletionError: incomplete }
        })
      }
    } catch (err) {
      dispatch({
        type: types.EXCEPTION,
        data: err?.response || { message: err?.message, err }
      })
    }
  }
}

export const fetchPartInputs = () => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation

      const currentElements = pyp.parts.byId[currentPartId].elements
      const elementString = internals.getElementString(currentElements, pyp)
      const apiCall = apiMap[elementString] || apiMap.default
      const data = await apiCall(dispatch, currentPartId)

      const part = pyp.parts.byId[currentPartId]
      // Create a copy of the part object instead of mutating the state directly
      const partWithData = { ...part, partData: data }

      // Safely create elements array, filtering out undefined/null values
      const currentPartForInputs = pyp.parts?.byId?.[currentPartId]
      const elementsForInputs = currentPartForInputs && currentPartForInputs.elements && Array.isArray(currentPartForInputs.elements)
        ? currentPartForInputs.elements
            .map((elementId) => pyp.elements?.byId?.[elementId])
            .filter((element) => element !== undefined && element !== null)
        : []

      const n = normalize(
        {
          parts: [partWithData],
          partData: [data]
        },
        {
          parts: [partSchema],
          partData: [partDataSchema]
        }
      )

      dispatch({
        type: types.FETCHED_PART_INPUTS,
        data: {
          updatedPart: n.entities.parts,
          partData: { allIds: n.result.partData, byId: n.entities.partData },
          currentPartComplete: partIsComplete(
            elementsForInputs,
            data.data,
            isCourseBuilderWorkshop(pyp)
          )
        }
      })
    } catch (err) {
      dispatch({
        type: types.EXCEPTION,
        data: err?.response || { message: err?.message, err }
      })
    }
  }
}

export const showPartCompletionError = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({
        type: types.UPDATE_PART_INCOMPLETION_ERROR,
        data: { showIncompletionError: true         }
      })
    } catch (err) {
      dispatch({
        type: types.EXCEPTION,
        data: err?.response || { message: err?.message, err }
      })
    }
  }
}

export const partIsLoading = (partIsLoading) => {
  return async (dispatch) => {
    try {
      dispatch({
        type: types.PART_IS_LOADING,
        data: { partIsLoading }
      })
    } catch (err) {
      dispatch({
        type: types.EXCEPTION,
        data: err?.response || { message: err?.message, err }
      })
    }
  }
}

export const removePartCompletionErrors = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({
        type: types.UPDATE_PART_INCOMPLETION_ERROR,
        data: { showIncompletionError: false }
      })
    } catch (err) {
      dispatch({
        type: types.EXCEPTION,
        data: err?.response || { message: err?.message, err }
      })
    }
  }
}

export const initializePYP = (
  moduleTitle,
  courseName = "Pursue Your Purpose",
  courseId = null
) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const decodedModuleTitle = decodeURIComponent(moduleTitle || "")
      const numericCourseId = courseId != null ? Number(courseId) : null

      const alreadyOnSameCourse =
        pyp.initialized &&
        ((numericCourseId != null &&
          Number(pyp.navigation.currentCourseId) === numericCourseId) ||
          (numericCourseId == null &&
            (pyp.navigation.currentCourseName || "").toLowerCase() ===
              courseName.toLowerCase()))

      if (alreadyOnSameCourse && pyp.modules?.byId) {
        const existingModule = Object.values(pyp.modules.byId).find(
          (m) =>
            m.title === decodedModuleTitle ||
            m.title === moduleTitle
        )
        if (existingModule) {
          if (pyp.navigation.currentModuleId !== existingModule.id) {
            dispatch({
              type: types.NAVIGATE_TO_MODULE,
              data: { moduleId: existingModule.id }
            })
          }
          return
        }
      }

      dispatch({
        type: types.INITIALIZING_PYP
      })

      const courseRes = numericCourseId
        ? await dispatch(
            fetch(`/courses/${numericCourseId}`, {
              type: "GET"
            })
          )
        : await dispatch(
            fetch(`/courses`, {
              type: "GET",
              data: {
                params: {
                  name: courseName
                }
              }
            })
          )

      if (!courseRes?.data?.id) {
        throw new Error(`Course not found: ${courseName || numericCourseId}`)
      }

      const moduleRes = await dispatch(
        fetch(`/courses/${courseRes.data.id}/modules`, {
          type: "GET"
        })
      )
      preconnectModuleImageOrigins(moduleRes.data)

      const courseData = courseRes.data
      courseData.modules = moduleRes.data

      let matchedModuleFromTitle = (moduleRes.data || []).find((module) => {
        return (
          module.title === decodedModuleTitle ||
          module.title === moduleTitle
        )
      })

      if (!matchedModuleFromTitle && moduleRes.data?.length) {
        matchedModuleFromTitle = moduleRes.data[0]
      }

      let modulesNavigationMetaData

      if (!pyp.navigation.moduleNavigationMetaData) {
        modulesNavigationMetaData = moduleRes.data.map((module) => {
          return { id: module.id, lastVisitedPart: -1 }
        })
      } else {
        modulesNavigationMetaData = pyp.navigation.moduleNavigationMetaData
      }

      const normalizedData = normalize(
        {
          courses: [courseData],
          modules: moduleRes.data,
          navigationParts: modulesNavigationMetaData
        },
        {
          courses: [courseSchema],
          modules: [moduleSchema],
          navigationParts: [navigationPartSchema]
        }
      )

      let unlockedUserModulesRes = await dispatch(
        fetch(`/userModules`, {
          type: "GET"
        })
      )

      const courseModuleIds = (moduleRes.data || []).map((m) => m.id)
      const hasCourseModule = unlockedUserModulesRes.data.some((um) =>
        courseModuleIds.includes(um.module_id)
      )

      if (!hasCourseModule && moduleRes.data?.length) {
        await dispatch(
          fetch(`/userModules`, {
            type: "POST",
            data: {
              requestPayload: {
                moduleId: moduleRes.data[0].id
              }
            }
          })
        )

        unlockedUserModulesRes = await dispatch(
          fetch(`/userModules`, {
            type: "GET"
          })
        )
      }

      if (!matchedModuleFromTitle) {
        dispatch({
          type: types.EXCEPTION,
          data: {
            message: "PYP module title not found in course",
            moduleTitle: decodedModuleTitle,
            availableTitles: (moduleRes.data || []).map((m) => m.title)
          }
        })
        dispatch({
          type: types.INITIALIZED_PYP,
          data: {
            course: {
              allIds: normalizedData.result.courses || [],
              byId: normalizedData.entities.courses || {}
            },
            modules: {
              allIds: normalizedData.result.modules || [],
              byId: normalizedData.entities.modules || {}
            },
            currentModuleId: moduleRes.data?.[0]?.id ?? -1,
            currentCourseId: courseRes.data.id,
            currentCourseName: courseRes.data.name,
            unlockedUserModules: unlockedUserModulesRes.data,
            moduleNavigationMetaData: normalizedData.entities.navigationPart
          }
        })
        return
      }

      dispatch({
        type: types.INITIALIZED_PYP,
        data: {
          course: {
            allIds: normalizedData.result.courses,
            byId: normalizedData.entities.courses
          },
          modules: {
            allIds: normalizedData.result.modules,
            byId: normalizedData.entities.modules
          },
          currentModuleId: matchedModuleFromTitle.id,
          currentCourseId: courseRes.data.id,
          currentCourseName: courseRes.data.name,
          unlockedUserModules: unlockedUserModulesRes.data,
          moduleNavigationMetaData: normalizedData.entities.navigationPart
        }
      })
    } catch (error) {
      console.error(
        "[initializePYP]",
        error?.response?.status,
        error?.response?.data || error?.message
      )
      dispatch({
        type: types.EXCEPTION,
        data: error?.response || { message: error?.message, error }
      })
    }
  }
}

export const setLeadUserInput = (index) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      const currentPart = pyp.parts?.byId?.[currentPartId]
      const currentPartData = currentPart?.partData && pyp.partData?.byId?.[currentPart.partData]
      const currentElements = currentPart && currentPart.elements && Array.isArray(currentPart.elements)
        ? currentPart.elements
            .map((elementId) => pyp.elements?.byId?.[elementId])
            .filter((element) => element !== undefined && element !== null)
        : []

      const newReferenceInputs = currentPartData.data.referenceInputs.map(
        (input, i) => {
          return {
            ...input,
            buttonSelectionTitle: {
              ...input.buttonSelectionTitle,
              is_lead: index === i
            }
          }
        }
      )

      const partData = {
        ...pyp.partData,
        byId: {
          ...pyp.partData.byId,
          [currentPartData.id]: {
            ...pyp.partData.byId[currentPartData.id],
            data: {
              ...pyp.partData.byId[currentPartData.id].data,
              referenceInputs: newReferenceInputs
            }
          }
        }
      }

      dispatch({
        type: types.SET_PYP_REFERENCE_INPUTS,
        data: {
          // referenceInputs: newReferenceInputs,
          partData,
          currentPartComplete: partIsComplete(
            currentElements,
            partData.byId[currentPartData.id].data,
            isCourseBuilderWorkshop(pyp)
          )
        }
      })
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const setButtonSelection = (buttonSelections) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      const currentPart = pyp.parts?.byId?.[currentPartId]
      const currentPartData = currentPart?.partData && pyp.partData?.byId?.[currentPart.partData]
      const currentElements = currentPart && currentPart.elements && Array.isArray(currentPart.elements)
        ? currentPart.elements
            .map((elementId) => pyp.elements?.byId?.[elementId])
            .filter((element) => element !== undefined && element !== null)
        : []

      const partData = {
        ...pyp.partData,
        byId: {
          ...pyp.partData.byId,
          [currentPartData.id]: {
            ...pyp.partData.byId[currentPartData.id],
            data: {
              ...pyp.partData.byId[currentPartData.id].data,
              valuesToUpdate: buttonSelections.value,
              selectedItems: buttonSelections.selectedItems
            }
          }
        }
      }

      dispatch({
        type: types.SET_PYP_BUTTON_SELECTION,
        data: {
          buttonSelections, // buttonSelections[0].value,
          currentPartComplete: partIsComplete(
            currentElements,
            partData.byId[currentPartData.id].data,
            isCourseBuilderWorkshop(pyp)
          )
        }
      })
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const retrieveAllPYPModules = (course_id) => {
  return async (dispatch, getState) => {
    try {
      const token = getState().api.apiToken
      apiRequest.defaults.headers.common.Authorization = token

      const courseRes = await apiRequest.get(`/courses`, {
        params: {
          name: "Pursue Your Purpose"
        }
      })

      const moduleRes = await apiRequest.get(
        `/courses/${courseRes.data.id}/modules`,
        {
          type: "GET"
        }
      )
      preconnectModuleImageOrigins(moduleRes.data)

      const normalizedModules = normalize(
        {
          modules: moduleRes.data
        },
        {
          modules: [moduleSchema]
        }
      )

      const data = {
        allIds: normalizedModules.result.modules,
        byId: normalizedModules.entities.modules
      }

      dispatch({
        type: types.FETCHED_MODULES,
        data
      })
    } catch (response) {
      dispatch({
        type: types.FETCHED_MODULES,
        data: []
      })
    }
  }
}

export const retrieveAllUserModulesWithParts = () => {
  return async (dispatch, getState) => {
    try {
      const courseRes = await dispatch(
        fetch(`/courses`, {
          type: "GET",
          data: {
            params: {
              name: "Pursue Your Purpose"
            }
          }
        })
      )

      const allModulesRes = await dispatch(
        fetch(`/courses/${courseRes.data.id}/modules`, {
          type: "GET"
        })
      )
      preconnectModuleImageOrigins(allModulesRes.data)

      const userModulesRes = await dispatch(
        fetch(`/userModules`, {
          type: "GET"
        })
      )

      const filteredModules = allModulesRes.data.filter((item) =>
        userModulesRes.data.find(
          (userModule) => userModule.module_id === item.id
        )
      )

      const retrievePartIds = async (moduleId) => {
        return await dispatch(
          fetch(`/courses/${courseRes.data.id}/modules/${moduleId}/parts`, {
            type: "GET"
          })
        )
      }

      const modulePartIds = filteredModules.map((singleModule) =>
        retrievePartIds(singleModule.id)
      )

      const promisedParts = await Promise.all(modulePartIds)

      const modulesWithParts = filteredModules.map((singleModule, index) => {
        return {
          // ...singleModule,
          id: singleModule.id,
          title: singleModule.title,
          parts_sequence: singleModule.parts_sequence,
          parts: promisedParts[index].data
        }
      })

      const metaParts = modulesWithParts.map(async (singleModule) => {
        const populatedParts = singleModule.parts.map(async (part) => {
          const metaResponse = await fetchMetaDataUserInputData(
            dispatch,
            part.id
          )
          return metaResponse.data
        })

        const promisedPopulatedParts = await Promise.all(populatedParts)

        const formattedPromisedPopulatedParts = promisedPopulatedParts.map(
          (part) => ({
            inputs:
              part.inputs.length &&
              part.inputs.map((input) => ({
                text: input.text,
                title: input.title && input.title.title,
                buttonTitle:
                  input.buttonSelectionTitle &&
                  input.buttonSelectionTitle.buttonValue.name
              })),
            title: part.title.length && part.title[0].title,
            description: part.title.length && part.title[0].description
          })
        )

        return formattedPromisedPopulatedParts
      })

      const promisedMetaParts = await Promise.all(metaParts)

      const modulesWithPartData = modulesWithParts.map(
        (singleModule, index) => {
          return {
            ...singleModule,
            parts: promisedMetaParts[index]
          }
        }
      )

      const modulesWithRenderableParts = modulesWithPartData.map(
        (singleModule) => {
          return {
            ...singleModule,
            parts: singleModule.parts.filter((part) => part.inputs.length)
          }
        }
      )
      return modulesWithRenderableParts
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const retrieveUserModulesWithPopulatedInputs = () => {
  return async (dispatch, getState) => {
    try {
      const populatedRes = await dispatch(
        fetch("/userModuleData", {
          type: "GET"
        })
      )
      return populatedRes.data
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const retrieveCourseModulesWithPopulatedInputs = (courseId) => {
  return async (dispatch) => {
    try {
      const populatedRes = await dispatch(
        fetch("/courseModuleData", {
          type: "GET",
          data: {
            params: { courseId }
          }
        })
      )
      return populatedRes.data
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const retrieveUsersTimeEventsByPartId = (partId) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      let currentPartId
      if (partId) {
        currentPartId = partId
      } else {
        currentPartId = pyp.navigation && pyp.navigation.currentPartId
      }
      const timeEvents = await dispatch(
        fetch("/timeEvents", {
          type: "GET",
          data: {
            params: { partId: currentPartId }
          }
        })
      )
      return timeEvents.data
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const storeNewTimeEvent = (eventData) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      const { title, duration, value_id } = eventData
      const eventResponse = await dispatch(
        fetch("/timeEvents", {
          type: "POST",
          data: {
            requestPayload: {
              params: {
                partId: currentPartId,
                events: [
                  {
                    title,
                    duration,
                    value_id
                  }
                ]
              }
            }
          }
        })
      )

      return eventResponse
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const storeUpdatedTimeEvent = (eventData) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      const eventResponse = await dispatch(
        fetch(`/timeEvents/${currentPartId}`, {
          type: "PUT",
          data: {
            requestPayload: {
              event: { ...eventData }
            }
          }
        })
      )

      return eventResponse
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const deleteDateEvent = (eventData) => {
  return async (dispatch, getState) => {
    try {
      const eventResponse = await dispatch(
        fetch(`/dateEvent/${eventData.id}`, {
          type: "DELETE"
        })
      )

      return eventResponse
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const storeUpdatedDateEvent = (eventData) => {
  return async (dispatch, getState) => {
    try {
      const eventResponse = await dispatch(
        fetch(`/dateEvent/${eventData.id}`, {
          type: "PUT",
          data: {
            requestPayload: {
              event: { ...eventData }
            }
          }
        })
      )

      return eventResponse
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const updateEventOrder = (updatedEventOrder) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      await dispatch(
        fetch(`/eventOrder/${currentPartId}`, {
          type: "PUT",
          data: { requestPayload: { inputs: updatedEventOrder } }
        })
      )
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const deleteTimeEvent = (eventData) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      const eventResponse = await dispatch(
        fetch(`/part/${currentPartId}/event/${eventData.id}`, {
          type: "DELETE"
        })
      )

      return eventResponse
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const createDateEventAndUpdateOrder = (allEventData, editedEvent) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      if (allEventData.length > 1) {
        if (editedEvent) {
          await dispatch(
            fetch(`/dateEvent/${editedEvent.id}`, {
              type: "PUT",
              data: {
                requestPayload: {
                  event: { ...editedEvent }
                }
              }
            })
          )
        }
        const eventResponse = await dispatch(
          fetch(`/parts/${currentPartId}/dateEvent`, {
            type: "POST",
            data: {
              requestPayload: {
                event: allEventData[allEventData.length - 1]
              }
            }
          })
        )
        const updatedEventOrder = allEventData.map((event) => event.id)
        updatedEventOrder[updatedEventOrder.length - 1] = eventResponse.data.id
        await dispatch(
          fetch(`/eventOrder/${currentPartId}`, {
            type: "PUT",
            data: { requestPayload: { inputs: updatedEventOrder } }
          })
        )
        return updateEventOrder
      }
      await dispatch(
        fetch(`/dateEvent`, {
          type: "POST",
          data: {
            requestPayload: {
              partId: currentPartId,
              event: {
                ...allEventData[allEventData.length - 1]
              }
            }
          }
        })
      )
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const createTimeEventAndUpdateOrder = (
  allEventData,
  editedEvent,
  eventOrder
) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation
      if (allEventData.length > 1) {
        if (editedEvent) {
          await dispatch(
            fetch(`/timeEvents/${currentPartId}`, {
              type: "PUT",
              data: {
                requestPayload: {
                  event: { ...editedEvent }
                }
              }
            })
          )
        }
        const eventResponse = await dispatch(
          fetch(`/parts/${currentPartId}/timeEvent`, {
            type: "POST",
            data: {
              requestPayload: {
                event: allEventData[allEventData.length - 1]
              }
            }
          })
        )
        // eventOrder[eventOrder.length - 1] = eventResponse.data.id
        allEventData.sort((a, b) => {
          if (a.id === 0) {
            return 1
          }
          if (b.id === 0) {
            return -1
          }
          return eventOrder.indexOf(a.id) - eventOrder.indexOf(b.id) > 0
            ? 1
            : -1
        })
        const updatedEventOrder = allEventData.map((event) => event.id)
        updatedEventOrder[updatedEventOrder.length - 1] = eventResponse.data.id
        await dispatch(
          fetch(`/eventOrder/${currentPartId}`, {
            type: "PUT",
            data: { requestPayload: { inputs: updatedEventOrder } }
          })
        )
        return updateEventOrder
      }
      await dispatch(
        fetch(`/timeEvents`, {
          type: "POST",
          data: {
            requestPayload: {
              partId: currentPartId,
              events: [
                {
                  ...allEventData[allEventData.length - 1],
                  value_id: allEventData[allEventData.length - 1].value_id
                }
              ]
            }
          }
        })
      )
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

// requires both time constraints to be sent through
export const storeTimeConstraints = ({ constraint1, constraint2 }) => {
  return async (dispatch, getState) => {
    try {
      const { pyp } = getState()
      const { currentPartId } = pyp.navigation

      dispatch({
        type: types.SAVING_PYP
      })

      await dispatch(
        fetch(`/inputSingleLine`, {
          type: "POST",
          data: {
            requestPayload: {
              partId: currentPartId,
              inputs: [constraint1, constraint2]
            }
          }
        })
      )

      dispatch({
        type: types.SAVED_PYP
      })
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
    // localhost:3333/v1/inputSingleLine
  }
}

export const updateSingleLineInputById = ({ newValue, id }) => {
  return async (dispatch, getState) => {
    try {
      await dispatch(
        fetch(`/inputSingleLine/byId/${id}`, {
          type: "PUT",
          data: {
            requestPayload: {
              text: newValue
            }
          }
        })
      )
    } catch (response) {
      dispatch({
        type: types.EXCEPTION,
        data: {
          message: response
        }
      })
    }
  }
}

export const finishPyp = (moduleId) => async (dispatch) => {
  await dispatch(
    fetch(`/finishPyp`, {
      type: "POST",
      data: {
        requestPayload: {
          moduleId
        }
      }
    })
  )
}
